import React, { useCallback, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import MapToolbar from './MapToolBar';
import FloatingPanel from './FloatingPanel';
import ProcessingDeliveryModal from './ProcessingDeliveryModal';
import useCatalogOrder from './useCatalogOrder';
import { useHistory } from 'react-router-dom';
import 'leaflet-geometryutil';
import MapSearchBox from './MapSearchBox';
import * as turf from '@turf/turf';
import 'leaflet.heat';


import { SEARCH_URLS, AOI_URLS, IMAGE_URLS, BASE_URL, SATELLITE_URLS } from '../config/urls';

// Helper function to get tasking date range (week from today)
const getTaskingDateRange = () => {
  const today = new Date();
  const endDate = new Date(today);
  endDate.setDate(today.getDate() + 7); // 7 days from today
  
  // Format as YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  return {
    startDate: formatDate(today),
    endDate: formatDate(endDate)
  };
};

// Helper function to check if date range matches tasking pattern
const isTaskingDateRange = (startDate, endDate) => {
  const taskingRange = getTaskingDateRange();
  return startDate === taskingRange.startDate && endDate === taskingRange.endDate;
};

// Helper functions to get SVG icon strings for HTML tooltips
const getClockIconSVG = (size = 12, color = '#fff') => {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
    <path d="M12 2.25C6.61522 2.25 2.25 6.61522 2.25 12C2.25 17.3848 6.61522 21.75 12 21.75C17.3848 21.75 21.75 17.3848 21.75 12C21.75 6.61522 17.3848 2.25 12 2.25ZM12 20.25C7.44365 20.25 3.75 16.5563 3.75 12C3.75 7.44365 7.44365 3.75 12 3.75C16.5563 3.75 20.25 7.44365 20.25 12C20.25 16.5563 16.5563 20.25 12 20.25ZM12.75 7.5C12.75 7.30109 12.671 7.11032 12.5303 6.96967C12.3897 6.82902 12.1989 6.75 12 6.75C11.8011 6.75 11.6103 6.82902 11.4697 6.96967C11.329 7.11032 11.25 7.30109 11.25 7.5V12C11.25 12.1989 11.329 12.3897 11.4697 12.5303C11.6103 12.671 11.8011 12.75 12 12.75H16.5C16.6989 12.75 16.8897 12.671 17.0303 12.5303C17.171 12.3897 17.25 12.1989 17.25 12C17.25 11.8011 17.171 11.6103 17.0303 11.4697C16.8897 11.329 16.6989 11.25 16.5 11.25H12.75V7.5Z" fill="${color}"/>
  </svg>`;
};

const getCalendarIconSVG = (size = 12, color = '#fff') => {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
    <path d="M19.5 3H17.25V2.25C17.25 2.05109 17.171 1.86032 17.0303 1.71967C16.8897 1.57902 16.6989 1.5 16.5 1.5C16.3011 1.5 16.1103 1.57902 15.9697 1.71967C15.829 1.86032 15.75 2.05109 15.75 2.25V3H8.25V2.25C8.25 2.05109 8.17098 1.86032 8.03033 1.71967C7.88968 1.57902 7.69891 1.5 7.5 1.5C7.30109 1.5 7.11032 1.57902 6.96967 1.71967C6.82902 1.86032 6.75 2.05109 6.75 2.25V3H4.5C4.10218 3 3.72064 3.15804 3.43934 3.43934C3.15804 3.72064 3 4.10218 3 4.5V19.5C3 19.8978 3.15804 20.2794 3.43934 20.5607C3.72064 20.842 4.10218 21 4.5 21H19.5C19.8978 21 20.2794 20.842 20.5607 20.5607C20.842 20.2794 21 19.8978 21 19.5V4.5C21 4.10218 20.842 3.72064 20.5607 3.43934C20.2794 3.15804 19.8978 3 19.5 3ZM6.75 4.5V5.25C6.75 5.44891 6.82902 5.63968 6.96967 5.78033C7.11032 5.92098 7.30109 6 7.5 6C7.69891 6 7.88968 5.92098 8.03033 5.78033C8.17098 5.63968 8.25 5.44891 8.25 5.25V4.5H15.75V5.25C15.75 5.44891 15.829 5.63968 15.9697 5.78033C16.1103 5.92098 16.3011 6 16.5 6C16.6989 6 16.8897 5.92098 17.0303 5.78033C17.171 5.63968 17.25 5.44891 17.25 5.25V4.5H19.5V7.5H4.5V4.5H6.75ZM19.5 19.5H4.5V9H19.5V19.5Z" fill="${color}"/>
  </svg>`;
};

const getSunElevationIconSVG = (size = 12, color = '#fff') => {
  // Generate a unique ID for each icon instance to avoid clipPath conflicts
  const uniqueId = `sun-elevation-clip-${Math.random().toString(36).substr(2, 9)}`;
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
    <g clip-path="url(#${uniqueId})">
      <path d="M11.25 3.75V1.5C11.25 1.30109 11.329 1.11032 11.4697 0.96967C11.6103 0.829018 11.8011 0.75 12 0.75C12.1989 0.75 12.3897 0.829018 12.5303 0.96967C12.671 1.11032 12.75 1.30109 12.75 1.5V3.75C12.75 3.94891 12.671 4.13968 12.5303 4.28033C12.3897 4.42098 12.1989 4.5 12 4.5C11.8011 4.5 11.6103 4.42098 11.4697 4.28033C11.329 4.13968 11.25 3.94891 11.25 3.75ZM18 12C18 13.1867 17.6481 14.3467 16.9888 15.3334C16.3295 16.3201 15.3925 17.0892 14.2961 17.5433C13.1997 17.9974 11.9933 18.1162 10.8295 17.8847C9.66557 17.6532 8.59647 17.0818 7.75736 16.2426C6.91824 15.4035 6.3468 14.3344 6.11529 13.1705C5.88378 12.0067 6.0026 10.8003 6.45672 9.7039C6.91085 8.60754 7.67988 7.67047 8.66658 7.01118C9.65327 6.35189 10.8133 6 12 6C13.5908 6.00174 15.1159 6.63444 16.2407 7.75928C17.3656 8.88412 17.9983 10.4092 18 12ZM16.5 12C16.5 11.11 16.2361 10.24 15.7416 9.49993C15.2471 8.75991 14.5443 8.18314 13.7221 7.84254C12.8998 7.50195 11.995 7.41283 11.1221 7.58647C10.2492 7.7601 9.44736 8.18868 8.81802 8.81802C8.18868 9.44736 7.7601 10.2492 7.58647 11.1221C7.41283 11.995 7.50195 12.8998 7.84254 13.7221C8.18314 14.5443 8.75991 15.2471 9.49993 15.7416C10.24 16.2361 11.11 16.5 12 16.5C13.1931 16.4988 14.337 16.0243 15.1806 15.1806C16.0243 14.337 16.4988 13.1931 16.5 12ZM5.46938 6.53063C5.61011 6.67136 5.80098 6.75042 6 6.75042C6.19902 6.75042 6.38989 6.67136 6.53063 6.53063C6.67136 6.38989 6.75042 6.19902 6.75042 6C6.75042 5.80098 6.67136 5.61011 6.53063 5.46938L5.03062 3.96938C4.88989 3.82864 4.69902 3.74958 4.5 3.74958C4.30098 3.74958 4.11011 3.82864 3.96938 3.96938C3.82864 4.11011 3.74958 4.30098 3.74958 4.5C3.74958 4.69902 3.82864 4.88989 3.96938 5.03062L5.46938 6.53063ZM5.46938 17.4694L3.96938 18.9694C3.82864 19.1101 3.74958 19.301 3.74958 19.5C3.74958 19.699 3.82864 19.8899 3.96938 20.0306C4.11011 20.1714 4.30098 20.2504 4.5 20.2504C4.69902 20.2504 4.88989 20.1714 5.03062 20.0306L6.53063 18.5306C6.60031 18.4609 6.65558 18.3782 6.6933 18.2872C6.73101 18.1961 6.75042 18.0985 6.75042 18C6.75042 17.9015 6.73101 17.8039 6.6933 17.7128C6.65558 17.6218 6.60031 17.5391 6.53063 17.4694C6.46094 17.3997 6.37822 17.3444 6.28717 17.3067C6.19613 17.269 6.09855 17.2496 6 17.2496C5.90145 17.2496 5.80387 17.269 5.71283 17.3067C5.62178 17.3444 5.53906 17.3997 5.46938 17.4694ZM18 6.75C18.0985 6.75008 18.1961 6.73074 18.2871 6.6931C18.3782 6.65546 18.4609 6.60025 18.5306 6.53063L20.0306 5.03062C20.1714 4.88989 20.2504 4.69902 20.2504 4.5C20.2504 4.30098 20.1714 4.11011 20.0306 3.96938C19.8899 3.82864 19.699 3.74958 19.5 3.74958C19.301 3.74958 19.1101 3.82864 18.9694 3.96938L17.4694 5.46938C17.3644 5.57427 17.2928 5.70796 17.2639 5.85352C17.2349 5.99908 17.2497 6.14998 17.3065 6.28709C17.3633 6.42421 17.4596 6.54139 17.583 6.62379C17.7065 6.70619 17.8516 6.75012 18 6.75ZM18.5306 17.4694C18.3899 17.3286 18.199 17.2496 18 17.2496C17.801 17.2496 17.6101 17.3286 17.4694 17.4694C17.3286 17.6101 17.2496 17.801 17.2496 18C17.2496 18.199 17.3286 18.3899 17.4694 18.5306L18.9694 20.0306C19.0391 20.1003 19.1218 20.1556 19.2128 20.1933C19.3039 20.231 19.4015 20.2504 19.5 20.2504C19.5985 20.2504 19.6961 20.231 19.7872 20.1933C19.8782 20.1556 19.9609 20.1003 20.0306 20.0306C20.1003 19.9609 20.1556 19.8782 20.1933 19.7872C20.231 19.6961 20.2504 19.5985 20.2504 19.5C20.2504 19.4015 20.231 19.3039 20.1933 19.2128C20.1556 19.1218 20.1003 19.0391 20.0306 18.9694L18.5306 17.4694ZM4.5 12C4.5 11.8011 4.42098 11.6103 4.28033 11.4697C4.13968 11.329 3.94891 11.25 3.75 11.25H1.5C1.30109 11.25 1.11032 11.329 0.96967 11.4697C0.829018 11.6103 0.75 11.8011 0.75 12C0.75 12.1989 0.829018 12.3897 0.96967 12.5303C1.11032 12.671 1.30109 12.75 1.5 12.75H3.75C3.94891 12.75 4.13968 12.671 4.28033 12.5303C4.42098 12.3897 4.5 12.1989 4.5 12ZM12 19.5C11.8011 19.5 11.6103 19.579 11.4697 19.7197C11.329 19.8603 11.25 20.0511 11.25 20.25V22.5C11.25 22.6989 11.329 22.8897 11.4697 23.0303C11.6103 23.171 11.8011 23.25 12 23.25C12.1989 23.25 12.3897 23.171 12.5303 23.0303C12.671 22.8897 12.75 22.6989 12.75 22.5V20.25C12.75 20.0511 12.671 19.8603 12.5303 19.7197C12.3897 19.579 12.1989 19.5 12 19.5ZM22.5 11.25H20.25C20.0511 11.25 19.8603 11.329 19.7197 11.4697C19.579 11.6103 19.5 11.8011 19.5 12C19.5 12.1989 19.579 12.3897 19.7197 12.5303C19.8603 12.671 20.0511 12.75 20.25 12.75H22.5C22.6989 12.75 22.8897 12.671 23.0303 12.5303C23.171 12.3897 23.25 12.1989 23.25 12C23.25 11.8011 23.171 11.6103 23.0303 11.4697C22.8897 11.329 22.6989 11.25 22.5 11.25Z" fill="${color}"/>
    </g>
    <defs>
      <clipPath id="${uniqueId}">
        <rect width="24" height="24" fill="white"/>
      </clipPath>
    </defs>
  </svg>`;
};

const getCloudIconSVG = (size = 12, color = '#fff') => {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
    <path d="M15.0001 3.75C13.4679 3.75118 11.9663 4.17857 10.6631 4.98437C9.35997 5.79017 8.30667 6.94262 7.62103 8.31281C6.80536 8.19385 5.97394 8.24424 5.17861 8.46082C4.38328 8.67741 3.64111 9.05555 2.99839 9.57166C2.35567 10.0878 1.82618 10.7308 1.44297 11.4606C1.05975 12.1904 0.831014 12.9913 0.771033 13.8134C0.711053 14.6355 0.82111 15.4612 1.09434 16.2388C1.36757 17.0165 1.79812 17.7296 2.35912 18.3335C2.92012 18.9375 3.59955 19.4193 4.35502 19.749C5.11049 20.0788 5.9258 20.2493 6.75009 20.25H15.0001C17.1881 20.25 19.2865 19.3808 20.8337 17.8336C22.3809 16.2865 23.2501 14.188 23.2501 12C23.2501 9.81196 22.3809 7.71354 20.8337 6.16637C19.2865 4.61919 17.1881 3.75 15.0001 3.75ZM15.0001 18.75H6.75009C5.55661 18.75 4.41202 18.2759 3.56811 17.432C2.72419 16.5881 2.25009 15.4435 2.25009 14.25C2.25009 13.0565 2.72419 11.9119 3.56811 11.068C4.41202 10.2241 5.55661 9.75 6.75009 9.75C6.85321 9.75 6.95634 9.75 7.05853 9.76031C6.85342 10.4892 6.74963 11.2428 6.75009 12C6.75009 12.1989 6.82911 12.3897 6.96976 12.5303C7.11041 12.671 7.30118 12.75 7.50009 12.75C7.699 12.75 7.88977 12.671 8.03042 12.5303C8.17107 12.3897 8.25009 12.1989 8.25009 12C8.25009 10.665 8.64597 9.35993 9.38767 8.2499C10.1294 7.13987 11.1836 6.2747 12.417 5.76381C13.6504 5.25292 15.0076 5.11925 16.3169 5.3797C17.6263 5.64015 18.8291 6.28302 19.7731 7.22703C20.7171 8.17103 21.3599 9.37377 21.6204 10.6831C21.8808 11.9925 21.7472 13.3497 21.2363 14.5831C20.7254 15.8165 19.8602 16.8707 18.7502 17.6124C17.6402 18.3541 16.3351 18.75 15.0001 18.75Z" fill="${color}"/>
  </svg>`;
};

const getOffNadirIconSVG = (size = 12, color = '#fff') => {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
    <path d="M9 6.75C9 6.55109 9.07902 6.36032 9.21967 6.21967C9.36032 6.07902 9.55109 6 9.75 6C12.335 6.00273 14.8134 7.03084 16.6413 8.85872C18.4692 10.6866 19.4973 13.165 19.5 15.75C19.5 15.9489 19.421 16.1397 19.2803 16.2803C19.1397 16.421 18.9489 16.5 18.75 16.5C18.5511 16.5 18.3603 16.421 18.2197 16.2803C18.079 16.1397 18 15.9489 18 15.75C17.9975 13.5627 17.1275 11.4657 15.5809 9.91911C14.0343 8.37247 11.9373 7.50248 9.75 7.5C9.55109 7.5 9.36032 7.42098 9.21967 7.28033C9.07902 7.13968 9 6.94891 9 6.75ZM22.5 18H7.5V3C7.5 2.80109 7.42098 2.61032 7.28033 2.46967C7.13968 2.32902 6.94891 2.25 6.75 2.25C6.55109 2.25 6.36032 2.32902 6.21967 2.46967C6.07902 2.61032 6 2.80109 6 3V6H3C2.80109 6 2.61032 6.07902 2.46967 6.21967C2.32902 6.36032 2.25 6.55109 2.25 6.75C2.25 6.94891 2.32902 7.13968 2.46967 7.28033C2.61032 7.42098 2.80109 7.5 3 7.5H6V18.75C6 18.9489 6.07902 19.1397 6.21967 19.2803C6.36032 19.421 6.55109 19.5 6.75 19.5H22.5C22.6989 19.5 22.8897 19.421 23.0303 19.2803C23.171 19.1397 23.25 18.9489 23.25 18.75C23.25 18.5511 23.171 18.3603 23.0303 18.2197C22.8897 18.079 22.6989 18 22.5 18Z" fill="${color}"/>
  </svg>`;
};

const getCoverageIconSVG = (size = 12, color = '#fff') => {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
    <path d="M16.3715 7.62889C16.1221 6.28556 15.5105 5.03588 14.6027 4.01479C13.6949 2.9937 12.5254 2.23999 11.2205 1.83504C9.91556 1.43009 8.52483 1.38928 7.19843 1.71703C5.87204 2.04478 4.66037 2.72863 3.69426 3.69475C2.72815 4.66086 2.0443 5.87253 1.71655 7.19892C1.3888 8.52532 1.4296 9.91605 1.83455 11.2209C2.2395 12.5258 2.99321 13.6953 4.0143 14.6031C5.0354 15.511 6.28507 16.1226 7.6284 16.372C7.87782 17.7153 8.48946 18.965 9.39727 19.9861C10.3051 21.0072 11.4746 21.7609 12.7795 22.1659C14.0844 22.5708 15.4751 22.6116 16.8015 22.2839C18.1279 21.9561 19.3396 21.2723 20.3057 20.3062C21.2718 19.34 21.9556 18.1284 22.2834 16.802C22.6111 15.4756 22.5703 14.0849 22.1654 12.78C21.7604 11.4751 21.0067 10.3056 19.9856 9.39776C18.9645 8.48995 17.7149 7.87831 16.3715 7.62889ZM21 15.0005C21 15.143 20.9934 15.2817 20.9831 15.4233L16.2956 10.7358C16.4174 10.2283 16.4859 9.7096 16.5 9.18795C17.7875 9.52223 18.9278 10.2742 19.7421 11.3261C20.5565 12.3779 20.9989 13.6702 21 15.0005ZM19.7372 18.6764L14.804 13.7442C15.1685 13.2988 15.4809 12.8131 15.735 12.2967L20.5968 17.1567C20.3859 17.7025 20.0963 18.2145 19.7372 18.6764ZM17.1562 20.5973L12.2943 15.7355C12.8108 15.4814 13.2964 15.169 13.7418 14.8045L18.674 19.7376C18.2127 20.0966 17.7013 20.3862 17.1562 20.5973ZM4.26278 5.32452L9.1959 10.2567C8.83141 10.7021 8.51907 11.1878 8.26497 11.7042L3.40309 6.8442C3.61402 6.29843 3.90365 5.78645 4.26278 5.32452ZM6.84371 3.40358L11.7056 8.26545C11.1892 8.51956 10.7035 8.8319 10.2581 9.19639L5.32403 4.26327C5.78596 3.90414 6.29794 3.61451 6.84371 3.40358ZM15 9.00045C14.9982 10.5912 14.3655 12.1163 13.2407 13.2412C12.1158 14.366 10.5907 14.9987 8.99996 15.0005C9.0017 13.4097 9.6344 11.8846 10.7592 10.7597C11.8841 9.63489 13.4092 9.00219 15 9.00045ZM14.8125 7.50045C14.2916 7.51314 13.7735 7.58006 13.2665 7.70014L8.57903 3.01264C8.71871 3.00702 8.85746 3.00045 8.99996 3.00045C10.3308 3.00182 11.6236 3.44487 12.6755 4.26012C13.7275 5.07537 14.4791 6.21671 14.8125 7.50514V7.50045ZM2.99996 9.00045C2.99996 8.85795 3.00653 8.7192 3.01684 8.57764L7.70434 13.2651C7.58413 13.7727 7.51721 14.2915 7.50465 14.813C6.21622 14.4796 5.07488 13.728 4.25963 12.676C3.44438 11.6241 3.00133 10.3313 2.99996 9.00045ZM9.18746 16.5005C9.70831 16.4878 10.2264 16.4208 10.7334 16.3008L15.4209 20.9883C15.2812 20.9986 15.1397 21.0051 14.9981 21.0051C13.6675 21.0034 12.3752 20.5601 11.3236 19.7449C10.2721 18.9297 9.52077 17.7886 9.18746 16.5005Z" fill="${color}"/>
  </svg>`;
};

const ExplorePage = () => {
  const history = useHistory();
  let resetFiltersRef = useRef(null);
  const mapContainerRef = useRef(null);
  const feasibilityTriggerRef = useRef(null); // Store feasibility trigger function from PanelContent
  const clearSelectionsRef = useRef(null); // Store clear selections function from PanelContent
  const clearTaskingResultsRef = useRef(null); // Store clear results function from PanelContent
  const [showPanel, setShowPanel] = useState(true);
  const [map, setMap] = useState(null);
  const [baseLayers, setBaseLayers] = useState({});
  const [activeLayer, setActiveLayer] = useState('Satellite');
  const [drawMode, setDrawMode] = useState(null);
  const drawModeRef = useRef(null);
  const handleClearImagery = () => {
      setImageryData([]);        // Clear catalog imagery
      setTaskingData([]);        // Optional: clear tasking also
    };

  useEffect(() => {
    drawModeRef.current = drawMode;
    
    // Close all popups when measurement mode is activated
    if (drawMode === 'measure' && map) {
      map.closePopup();
      map.eachLayer((layer) => {
        if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
          try {
            layer.closePopup();
          } catch (e) {
            // Ignore errors
          }
        }
      });
    }
  }, [drawMode, map]);
  const [imageryData, setImageryData] = useState([]);
  const [drawnCoordinates, setDrawnCoordinates] = useState(null);
  // Centralized date range state - single source of truth for all queries
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false); 
  const [activeTab, setActiveTab] = useState('catalog');  
// console.log(activeTab)
  const [taskingData, setTaskingData] = useState(null);

  // States for interaction
  const [hoveredImage, setHoveredImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [visibleImage, setVisibleImage] = useState(null);
  const [selectedPolygons, setSelectedPolygons] = useState({});
  const [previewLayer, setPreviewLayer] = useState(null);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [activeAOIPolygon, setActiveAOIPolygon] = useState(null);


  const [footprintCoordinates, setFootprintCoordinates] = useState(null);
  const [footprintData, setFootprintData] = useState(null);  // Single constellation (for backward compatibility)
  const [footprintsByConstellation, setFootprintsByConstellation] = useState({});  // Multiple constellations: { constellationName: { coordinates, color, opportunities, timezone, off_nadir_limit } }
  
  // Request tracking to prevent duplicate calls
  const currentRequestId = useRef(null);
  const requestCounter = useRef(0);
  const isSearchingRef = useRef(false);
  const catalogDatesRef = useRef({ startDate: '', endDate: '' });
  
  // Store catalog state when switching away from catalog tab
  const catalogStateRef = useRef({
    dateRange: null,
    drawnCoordinates: null, // Store AOI coordinates to detect changes
    imageryData: [],
    selectedImages: [],
    visibleImage: null,
    selectedPolygons: {},
    satelliteVisibility: {},
    hasState: false // Flag to track if we have stored state
  });
  const trackLayerRef = useRef(null);
  const highlightedFootprintRef = useRef(null);
  const footprintPopupStyleAddedRef = useRef(false);
  const trackLoadingRef = useRef(false);

  const ensureFootprintPopupStyles = useCallback(() => {
    if (footprintPopupStyleAddedRef.current) return;
    if (document.getElementById('footprint-popup-styles')) {
      footprintPopupStyleAddedRef.current = true;
      return;
    }

    const style = document.createElement('style');
    style.id = 'footprint-popup-styles';
    style.textContent = `
      .footprint-popup .leaflet-popup-content-wrapper {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
      }
      .footprint-popup .leaflet-popup-content {
        margin: 0 !important;
        padding: 0 !important;
      }
      .footprint-popup .leaflet-popup-tip {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
      }
      .footprint-popup .leaflet-popup-close-button {
        top: 6px;
        right: 8px;
        color: #f2f6ff;
        font-size: 16px;
        text-shadow: none;
        background: rgba(4, 4, 4, 0.3);
        border-radius: 6px;
        width: 20px;
        height: 20px;
        line-height: 16px;
        text-align: center;
      }
      .footprint-popup .leaflet-popup-close-button:hover {
        color: #9fc7ff;
      }
    `;
    document.head.appendChild(style);
    footprintPopupStyleAddedRef.current = true;
  }, []);

  const clearHighlightedFootprint = useCallback(() => {
    if (highlightedFootprintRef.current) {
      const layer = highlightedFootprintRef.current;
      if (layer._originalStyle) {
        layer.setStyle(layer._originalStyle);
      }
      highlightedFootprintRef.current = null;
    }
  }, []);

  const setMapCursor = useCallback(
    (cursorStyle = '') => {
      const container = map?.getContainer ? map.getContainer() : map?._container;
      if (container) {
        container.style.cursor = cursorStyle;
      }
    },
    [map]
  );

  const highlightFootprint = useCallback(
    (polygon, highlightColor = '#66B0FF') => {
      if (!polygon) return;

      if (highlightedFootprintRef.current && highlightedFootprintRef.current !== polygon) {
        clearHighlightedFootprint();
      }

      if (!polygon._originalStyle) {
        polygon._originalStyle = {
          color: polygon.options.color,
          weight: polygon.options.weight,
          fillOpacity: polygon.options.fillOpacity,
          dashArray: polygon.options.dashArray || null,
        };
      }

      polygon.setStyle({
        color: highlightColor,
        weight: 3,
        dashArray: '6 3',
        fillOpacity: Math.max(polygon._originalStyle.fillOpacity || 0.1, 0.18),
      });

      highlightedFootprintRef.current = polygon;
    },
    [clearHighlightedFootprint]
  );
  const clearTrackLayer = useCallback((options = {}) => {
    const { resetHighlight = true } = options;
    if (trackLayerRef.current && map) {
      try {
        map.removeLayer(trackLayerRef.current);
      } catch (err) {
        console.warn('Failed to remove track layer', err);
      }
      trackLayerRef.current = null;
    }
    trackLoadingRef.current = false;
    setMapCursor('');
    if (resetHighlight) {
      clearHighlightedFootprint();
    }
  }, [map, clearHighlightedFootprint, setMapCursor]);

  const renderTrackOnMap = useCallback(
    (points) => {
      if (!map || !points || points.length === 0) {
        return;
      }

      clearTrackLayer({ resetHighlight: false });

      const latLngs = points
        .filter((pt) => typeof pt.latitude === 'number' && typeof pt.longitude === 'number')
        .map((pt) => [pt.latitude, pt.longitude]);

      if (latLngs.length === 0) return;

      const trackLayer = L.polyline(latLngs, {
        color: '#FFFFFF',
        weight: 3,
        opacity: 0.95,
        dashArray: '6 4'
      }).addTo(map);

      trackLayerRef.current = trackLayer;

      const bounds = trackLayer.getBounds();
      if (bounds.isValid()) {
        const targetZoom = 8; // Fixed zoom level for track rendering
        const center = bounds.getCenter();
        map.flyTo(center, targetZoom, { duration: 0.6 });
      }
    },
    [map, clearTrackLayer]
  );

  useEffect(() => {
    return () => {
      clearTrackLayer();
    };
  }, [clearTrackLayer]);

  useEffect(() => {
    if (activeTab !== 'task') {
      clearTrackLayer();
    }
  }, [activeTab, clearTrackLayer]);

  // Flag to prevent search trigger when restoring catalog state
  const isRestoringCatalogStateRef = useRef(false);
  const isRestoringTaskingStateRef = useRef(false);
  
  // Simple ref to store tasking state - stores results when loaded
  const taskingStateRef = useRef({
    dateRange: null,
    drawnCoordinates: null,
    filterKey: null,
    taskingDetails: {},
    constellations: [],
    checkedConstellations: {},
    stripSelections: {}, // Store strip selections
    visibleConstellations: {}, // Store visibility state
    hasState: false,
  });
  
  // Flag to prevent search trigger when setting default dates for the first time
  const isSettingDefaultDatesRef = useRef(false);
  
  // Track previous active tab to detect tab switches
  const previousActiveTabRef = useRef('');
  
  // Track current active tab in a ref for reliable access (avoids stale closures)
  const activeTabRef = useRef('catalog');
  
  // Flag to track if user is actively drawing (prevent search during drawing)
  const isDrawingRef = useRef(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [satelliteVisibility, setSatelliteVisibility] = useState(() => {
    const visibilityState = {};
    if (imageryData) {
      imageryData.forEach(item => {
        if (item.satellite) {
          visibilityState[item.satellite] = true;
        }
      });
    }
    return visibilityState;
  });

  // Helper to persist catalog state (dates, imagery, selections)
  const storeCatalogState = useCallback((reason = 'unknown') => {
    if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
      console.log(`🗃️ Skipping catalog state save (${reason}) - missing date range`);
      return;
    }
    catalogStateRef.current = {
      dateRange,
      drawnCoordinates: drawnCoordinates ? JSON.parse(JSON.stringify(drawnCoordinates)) : null, // Store AOI coordinates
      imageryData,
      selectedImages,
      visibleImage,
      selectedPolygons,
      satelliteVisibility,
      hasState: true,
    };
    console.log(`💾 Catalog state stored (${reason})`, {
      dateRange,
      coordinatesLength: drawnCoordinates?.length,
    });
  }, [dateRange, drawnCoordinates, imageryData, selectedImages, visibleImage, selectedPolygons, satelliteVisibility]);

  // Add this useEffect to automatically render a specific polygon when switching to task tab
  // COMMENTED OUT: Bangalore auto-loading disabled
  // useEffect(() => {
  //   if (map && activeTab === 'task') {
  //     console.log('Tab switched to task - rendering specific polygon');
      
  //     // Define specific coordinates for Bangalore area
  //     const bangaloreCoordinates = [
  //       {"lat": 13.198594854154791, "lng": 77.23036500489718},
  //       {"lat": 12.68417581052806, "lng": 77.23036500489718},
  //       {"lat": 12.68417581052806, "lng": 77.8876216052203},
  //       {"lat": 13.198594854154791, "lng": 77.8876216052203}
  //     ];
      
  //     // Update state with these coordinates
  //     setDrawnCoordinates(bangaloreCoordinates);
      
  //     // Remove any existing drawn polygon layers
  //     map.eachLayer((layer) => {
  //       if (layer instanceof L.Polygon) {
  //         map.removeLayer(layer);
  //       }
  //     });
      
  //     // Create new polygon with tooltip
  //     const polygon = createPolygonWithTooltip(bangaloreCoordinates, map, "Bangalore Region");
  //     setActiveAOIPolygon(polygon);
      
  //     // Fit the map view to the polygon
  //     map.fitBounds(polygon.getBounds());
      
  //   }
  // }, [map, activeTab]); // Remove coordinates from dependencies to avoid circular updates
  
  const clearSelectedLayers = () => {
    if (map) {
      // Remove preview layer if it exists
      if (previewLayer) {
        map.removeLayer(previewLayer);
        setPreviewLayer(null);
      }
      
      // Remove each selected image/footprint polygon
      Object.values(selectedPolygons).forEach((layer) => {
        map.removeLayer(layer);
      });
      
      // Also remove any image overlays that were added directly to map
      Object.keys(map).forEach(key => {
        if (key.startsWith('overlay_') && map[key] instanceof L.ImageOverlay) {
          map.removeLayer(map[key]);
          delete map[key];
        }
      });
      
      // Clear the selected layers state (this does NOT include AOI polygon)
      setSelectedPolygons({});
      setSelectedImages([]);
      
      // Clear any visible or hovered image state
      setVisibleImage(null);
      setHoveredImage(null);
    }
  };
  
  // Listen for coordinate updates from MapToolBar and MapSearchBox
  useEffect(() => {
    const handleAOICoordinatesUpdate = (event) => {
      const { coordinates } = event.detail;
      if (coordinates && coordinates.length > 0) {
        // Update drawnCoordinates state
        setDrawnCoordinates(coordinates);
        
        // Create polygon with tooltip on the map (for uploaded files)
        // Only create if we don't already have an active polygon (to avoid duplicates when drawing)
        if (map && !isDrawingRef.current) {
          // Remove any existing drawn layers (including GeoJSON layers from file uploads)
          map.eachLayer((layer) => {
            // Remove GeoJSON layers (from file uploads) and old polygons
            if (layer instanceof L.GeoJSON) {
              map.removeLayer(layer);
            } else if (layer instanceof L.Polygon && activeAOIPolygon && layer !== activeAOIPolygon) {
              // Only remove polygons that aren't the active one
              map.removeLayer(layer);
            } else if (layer instanceof L.Polygon && !activeAOIPolygon) {
              // If no active polygon, remove any existing polygon
              map.removeLayer(layer);
            }
          });
          
          // Create new polygon with tooltip
          const polygon = createPolygonWithTooltip(coordinates, map, null);
          setActiveAOIPolygon(polygon);
          
          // Fit the map view to the polygon
          map.fitBounds(polygon.getBounds());
        }
        
        // Clear tasking-related state when AOI changes (if on tasking tab)
        if (activeTab === 'task') {
          // Clear footprint data
          setFootprintData(null);
          setFootprintsByConstellation({});  // Clear all constellation footprints
          setFootprintCoordinates(null);
          // Clear tasking data
          setTaskingData(null);
        }
        
        // If we have a date range, perform a new search/feasibility with updated coordinates
        if (dateRange && !isDrawingRef.current) {
          // On tasking tab, trigger feasibility instead of search
          if (activeTab === 'task') {
            console.log("🔄 [ExplorePage] AOI coordinates updated on tasking tab - triggering feasibility checks");
            // Clear selections when AOI changes
            if (clearSelectionsRef.current) {
              clearSelectionsRef.current();
            }
            // Trigger feasibility checks with the new coordinates
            setTimeout(() => {
              if (feasibilityTriggerRef.current) {
                // Pass the new coordinates directly to ensure latest AOI is used
                feasibilityTriggerRef.current(coordinates);
              } else {
                console.warn("⚠️ [ExplorePage] Feasibility trigger not available yet");
              }
            }, 200);
          } else if (activeTab === 'catalog') {
            // On catalog tab, trigger search API when AOI changes
            console.log("🔄 [ExplorePage] AOI coordinates updated on catalog tab - triggering search API", {
              coordinatesCount: coordinates.length,
              dateRange: dateRange,
            });
            // CRITICAL: Clear restoration flag when AOI changes via file upload
            // This ensures search can proceed even if restoration was in progress
            isRestoringCatalogStateRef.current = false;
            // Clear stored catalog state since AOI changed
            catalogStateRef.current.hasState = false;
            catalogStateRef.current.drawnCoordinates = null;
            // Trigger search with new coordinates
            handleSearchRequest(coordinates, dateRange);
          } else {
            console.log("AOI coordinates updated, triggering search");
            handleSearchRequest(coordinates, dateRange);
          }
        } else if (isDrawingRef.current) {
          console.log("AOI coordinates updated while drawing - deferring search/feasibility");
        } else if (!dateRange) {
          console.log("⏸️ [ExplorePage] AOI coordinates updated but no dateRange - skipping search");
        }
        
        console.log('AOI coordinates updated, resetting state');
      }
    };
    
    // Add event listener
    window.addEventListener('aoiCoordinatesUpdated', handleAOICoordinatesUpdate);
    
    // Clean up
    return () => {
      window.removeEventListener('aoiCoordinatesUpdated', handleAOICoordinatesUpdate);
    };
  }, [dateRange, map, activeTab]);

  const handleSatelliteVisibilityToggle = (satellite, isVisible) => {
    console.log('Toggle visibility for', satellite, 'to', isVisible);
    
    // Update visibility state first
    setSatelliteVisibility(prev => {
      const newState = {
        ...prev,
        [satellite]: isVisible
      };
      console.log('New visibility state:', newState);
      return newState;
    });
  
    // Handle polygon visibility and image overlays
    if (map) {
      // Handle regular polygons
      Object.entries(selectedPolygons).forEach(([id, polygon]) => {
        const image = selectedImages.find(img => img.id === id);
        if (image && image.satellite === satellite) {
          const hasLayer = map.hasLayer(polygon);
          console.log('Polygon', id, 'current map status:', hasLayer);
          
          try {
            if (!isVisible && hasLayer) {
              console.log('Removing polygon', id);
              map.removeLayer(polygon);
            } else if (isVisible && !hasLayer) {
              console.log('Adding polygon', id);
              polygon.addTo(map);
            }
          } catch (error) {
            console.error('Error toggling polygon visibility:', error);
          }
        }
      });
      
      // Handle image overlays that were added directly to the map
      Object.keys(map).forEach(key => {
        if (key.startsWith('overlay_')) {
          const imageId = key.replace('overlay_', '');
          const image = selectedImages.find(img => img.id === imageId);
          
          if (image && image.satellite === satellite) {
            try {
              if (!isVisible && map.hasLayer(map[key])) {
                map.removeLayer(map[key]);
              } else if (isVisible && !map.hasLayer(map[key])) {
                map[key].addTo(map);
              }
            } catch (error) {
              console.error('Error toggling image overlay visibility:', error);
            }
          }
        }
      });
      
      // If the currently visible preview image belongs to this satellite, hide it
      if (visibleImage && visibleImage.satellite === satellite && !isVisible) {
        if (previewLayer) {
          map.removeLayer(previewLayer);
          setPreviewLayer(null);
        }
        setVisibleImage(null);
      }
      
      // If the currently hovered image belongs to this satellite, hide its highlight
      if (hoveredImage && hoveredImage.satellite === satellite && !isVisible) {
        if (previewLayer) {
          map.removeLayer(previewLayer);
          setPreviewLayer(null);
        }
        setHoveredImage(null);
      }
    }
  };

  const handleSaveAOI = async () => {
    if (!drawnCoordinates) {
      alert('Draw an Area of Interest first');
      return;
    }
  
    try {
      const token = localStorage.getItem('token');
      const aoiName = prompt('Enter a name for this AOI (optional):') || undefined;
      
      const response = await fetch(AOI_URLS.saveAOI, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          coordinates: drawnCoordinates,
          name: aoiName
        })
      });
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 409) {
          alert(errorData.detail || 'An AOI with this name already exists. Please choose a different name.');
        } else {
          throw new Error(errorData.detail || 'Failed to save AOI');
        }
        return;
      }
  
      alert('AOI saved successfully');
    } catch (error) {
      console.error('Error saving AOI:', error);
      alert(error.message || 'Failed to save AOI');
    }
  };
  
  const handleSelectAOI = (coordinates, name) => {
    console.log("coordinates from aoiselect",JSON.stringify(coordinates))
    setDrawnCoordinates(coordinates);
    
    if (map) {
      // Remove any existing drawn layers
      map.eachLayer((layer) => {
        if (layer instanceof L.Polygon) {
          map.removeLayer(layer);
        }
      });
      console.log(name)
      // Create new polygon with tooltip, passing the name
      const polygon = createPolygonWithTooltip(coordinates, map, name);
      setActiveAOIPolygon(polygon);
      
      // Fit the map view to the polygon
      map.fitBounds(polygon.getBounds());
    }
    
    if (dateRange) {
      console.log("here2")
      handleSearchRequest(coordinates, dateRange);
    }
  };
  
  // New function to handle the edit event
  const handleAOIEdit = (e) => {
    if (!e.layer || !e.layer.getLatLngs) return;
    
    // Get the updated coordinates from the edited polygon
    const latLngs = e.layer.getLatLngs()[0];
    const updatedCoordinates = latLngs.map(latLng => ({
      lat: latLng.lat,
      lng: latLng.lng
    }));
    
    // Update drawnCoordinates state
    setDrawnCoordinates(updatedCoordinates);
    
    // If we have a date range, perform a new search with updated coordinates
    if (dateRange) {
      console.log("here3")
      handleSearchRequest(updatedCoordinates, dateRange);
    }
    
    console.log('AOI has been edited, updated coordinates:', updatedCoordinates);
  };
  
  // New function to handle AOI deletion
  const handleAOIDelete = () => {
    // Clear drawnCoordinates state
    setDrawnCoordinates(null);
    setActiveAOIPolygon(null);
    
    // Also clear search results since they're based on the AOI
    setImageryData([]);
    
    // Clear stored catalog state since it's tied to the deleted AOI
    catalogStateRef.current = {
      dateRange: null,
      imageryData: [],
      selectedImages: [],
      visibleImage: null,
      selectedPolygons: {},
      satelliteVisibility: {},
      hasState: false
    };
    
    console.log('AOI has been deleted');
  };

  const handleFootprintSelect = (data) => {
    console.log("handlefootprintselect data : ",JSON.stringify(data))
    console.log("checking if footprint data present in handleFootprintSelect: ", JSON.stringify(footprintData))
    clearTrackLayer();
    
    // If replaceAll flag is set, clear all footprints and only show this one
    if (data.replaceAll && data.constellation && data.coordinates && data.coordinates.length > 0) {
      // Store the new constellation's footprint data
      const newFootprints = {
        [data.constellation]: {
          coordinates: data.coordinates,
          color: data.color,
          constellation: data.constellation,
          opportunities: data.opportunities || [],
          timezone: data.timezone || 'UTC',
          off_nadir_limit: data.off_nadir_limit
        }
      };
      
      // If there are other visible constellations to restore, we'll do that via a callback
      // For now, just replace all with the new one
      setFootprintsByConstellation(newFootprints);
    }
    // If data has coordinates and constellation, add/update it in the footprints map (for eye icon toggles)
    else if (data.constellation && data.coordinates && data.coordinates.length > 0) {
      setFootprintsByConstellation(prev => ({
        ...prev,
        [data.constellation]: {
          coordinates: data.coordinates,
          color: data.color,
          constellation: data.constellation,
          opportunities: data.opportunities || [],
          timezone: data.timezone || 'UTC',
          off_nadir_limit: data.off_nadir_limit
        }
      }));
    } else if (data.constellation && (!data.coordinates || data.coordinates.length === 0)) {
      // Remove this constellation's footprints if coordinates are empty
      setFootprintsByConstellation(prev => {
        const updated = { ...prev };
        delete updated[data.constellation];
        return updated;
      });
    } else if (!data.constellation && (!data.coordinates || data.coordinates.length === 0)) {
      // Clear all footprints if no constellation is specified and coordinates are empty
      setFootprintsByConstellation({});
    }
    
    // Keep backward compatibility - set single footprintData for selected constellation
    setFootprintData({
      ...data,
      constellation: data.constellation // Added constellation name
    });
  };
  
  const handleFootprintPolygonClick = useCallback(async (constellationName, opportunity, color) => {
    if (!opportunity) {
      return;
    }

    const satelliteId =
      opportunity.satellite_id ||
      opportunity.satelliteId ||
      opportunity.satellite_identifier ||
      opportunity.norad_id;

    const timestampUtc = opportunity.access_start_utc || opportunity.access_start;

    if (!satelliteId || !timestampUtc) {
      console.warn('Missing satellite ID or timestamp for track request', {
        satelliteId,
        timestampUtc,
        opportunity,
      });
      return;
    }

    try {
      trackLoadingRef.current = true;
      setMapCursor('progress');
      const token = localStorage.getItem('token');
      const payload = {
        timestamp: timestampUtc,
        window_minutes: 10,
        satellite_id: String(satelliteId),
        constellation_name: constellationName,
      };

      const response = await fetch(SATELLITE_URLS.getTrack, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.error('Failed to fetch satellite track', await response.text());
        return;
      }

      const result = await response.json();
      if (Array.isArray(result?.points) && result.points.length > 0) {
        renderTrackOnMap(result.points, color);
      } else {
        clearTrackLayer();
      }
    } catch (error) {
      console.error('Error fetching satellite track', error);
    } finally {
      trackLoadingRef.current = false;
      setMapCursor('');
    }
  }, [clearTrackLayer, renderTrackOnMap, setMapCursor]);
  
  // Update the useEffect for polygon rendering - now supports multiple constellations
  useEffect(() => {
    if (!map) return;
    
    // Remove all existing footprint layers
    Object.values(selectedPolygons).forEach(layer => {
      if (layer) map.removeLayer(layer);
    });
  
    const newPolygons = {};
    const allPolygons = [];
  
    // Render footprints for all visible constellations
    Object.entries(footprintsByConstellation).forEach(([constellationName, constellationData]) => {
      if (!constellationData || !constellationData.coordinates || constellationData.coordinates.length === 0) {
        return;
      }
      
      constellationData.coordinates.forEach((coordSet, index) => {
      const flattenedCoords = coordSet.flatMap(coordArray => 
        coordArray.map(coord => [coord[0], coord[1]])
      );
  
        // Create tooltip content with constellation and timing info
        const opportunity = constellationData.opportunities[index];
        if (!opportunity) return;
        
        // Get timezone from constellationData or default to UTC
        const timezone = constellationData.timezone || 'UTC';
        
        // Format timestamp (access_start is in format: YYYY-MM-DDTHH:MM:SS)
        const formatTimestamp = (timestampStr) => {
          if (!timestampStr) return 'N/A';
          try {
            const [datePart, timePart] = timestampStr.split('T');
            const [year, month, day] = datePart.split('-');
            const [hour, minute, second] = timePart.split(':');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${day} ${monthNames[parseInt(month) - 1]} ${year}, ${hour}:${minute}:${second} ${timezone}`;
          } catch (e) {
            return timestampStr;
          }
        };
        
        // Get duration (already formatted to 2 decimals from backend)
        const duration = opportunity.duration_seconds || '0.00';

      const tooltipContent = `
  <div style="font-family: sans-serif; font-size: 12px; min-width: 210px; color: #A6B6CE; 
              background-color: rgba(22, 22, 22, 0.95); border-radius: 8px; 
              border: 1px solid #2E90FA; overflow: hidden;
              margin: 0; padding: 0;">
    <div style="padding: 10px; border-bottom: 1px solid rgba(46, 144, 250, 0.3);">
      <div style="display: flex; align-items: center; gap: 6px;">
  <div style="width: 10px; height: 10px; background-color: ${constellationData.color}; border-radius: 2px;"></div>
  <strong style="color: #fff;">${constellationName || 'Unknown'}</strong>
</div>

    </div>
    <div style="padding: 10px;">
      <p style="margin: 0 0 5px 0; display: flex; align-items: center; justify-content: space-between; gap: 8px;">
        <span style="font-weight: 600; font-size: 11px; display: flex; align-items: center; gap: 4px; color: #cdd8ea; opacity: 0.75;">${getCalendarIconSVG(12, '#cdd8ea')} Time</span>
        <span style="color: #f7fbff; font-size: 11px; letter-spacing: 0.2px; text-align: right;">${formatTimestamp(opportunity.access_start)}</span>
      </p>
      <div style="display: grid; grid-template-columns: auto auto; row-gap: 4px; column-gap: 18px; font-size: 11px; color: #cdd8ea;">
        <span style="opacity: 0.75; display: flex; align-items: center; gap: 4px;">${getCoverageIconSVG(12, '#cdd8ea')} Coverage</span><span style="color: #f7fbff;">${opportunity.coverage_percentage ? opportunity.coverage_percentage.toFixed(1) : '0.0'}%</span>
        <span style="opacity: 0.75; display: flex; align-items: center; gap: 4px;">${getOffNadirIconSVG(12, '#cdd8ea')} Off-Nadir</span><span style="color: #f7fbff;">${typeof opportunity.viewing_angle === 'number' ? opportunity.viewing_angle.toFixed(1) : (opportunity.viewing_angle || '0')}°</span>
        <span style="opacity: 0.75; display: flex; align-items: center; gap: 4px;">${getSunElevationIconSVG(12, '#cdd8ea')} Sun Elevation</span><span style="color: #f7fbff;">${typeof opportunity.sun_elevation === 'number' ? opportunity.sun_elevation.toFixed(1) : 'N/A'}°</span>
        <span style="opacity: 0.75; display: flex; align-items: center; gap: 4px;">${getClockIconSVG(12, '#cdd8ea')} Duration</span><span style="color: #f7fbff;">${duration} s</span>
      </div>
    </div>
  </div>
`;
    
      // Create polygon with tooltip
        const polygonKey = `${constellationName}_footprint_${index}`;
        ensureFootprintPopupStyles();

      const polygon = L.polygon(flattenedCoords, {
          color: constellationData.color,
          weight: 1.2,
          fillOpacity: 0.3,
          opacity: 0.95,
          className: 'tasking-footprint'
      })
        .bindPopup(tooltipContent, {
          maxWidth: 260,
          closeButton: true,
          autoPan: true,
          className: 'footprint-popup'
      })
      .addTo(map);
  
        polygon._originalStyle = {
          color: constellationData.color,
          weight: 1.2,
          fillOpacity: 0.08,
          dashArray: null,
        };

        polygon.on('popupclose', () => {
          clearTrackLayer();
        });

        polygon.on('click', () => {
          if (drawMode === 'measure') {
            return;
          }
          highlightFootprint(polygon, '#ffffff');
          polygon.openPopup();
          handleFootprintPolygonClick(constellationName, opportunity, constellationData.color);
        });
    
        newPolygons[polygonKey] = polygon;
        allPolygons.push(polygon);
      });
    });
  
    // Fit bounds to show all visible footprints
    // BUT: Don't fit bounds if user is actively drawing (prevents map jumping when clicking draw button)
    if (allPolygons.length > 0 && !isDrawingRef.current) {
      const featureGroup = L.featureGroup(allPolygons);
      map.fitBounds(featureGroup.getBounds());
    }
  
    setSelectedPolygons(newPolygons);
  
  }, [map, footprintsByConstellation, handleFootprintPolygonClick, highlightFootprint, ensureFootprintPopupStyles, drawMode]);
  
  // Use the catalog order hook
  const { createOrder, saveDraftOrder } = useCatalogOrder();
  

  // Map initialization
const createPolygonWithTooltip = (coordinates, map, name) => {
    console.log("createpolygonwithtooltip coordinates:",JSON.stringify(coordinates))
    console.log("createpolygonwithtooltip map:",map)
    console.log("createpolygonwithtooltip name:",name)

    
    // Ensure coordinates are properly formatted
    const formattedCoordinates = coordinates.map(coord => [
      coord.lat !== undefined ? coord.lat : coord.latitude, 
      coord.lng !== undefined ? coord.lng : coord.longitude
    ]);

    if (formattedCoordinates[0][0] !== formattedCoordinates[formattedCoordinates.length - 1][0] ||
      formattedCoordinates[0][1] !== formattedCoordinates[formattedCoordinates.length - 1][1]) {
      formattedCoordinates.push(formattedCoordinates[0]);  // Close the polygon by adding the first point as the last point
    }
  
    var polygon_turf = turf.polygon([
      formattedCoordinates
    ]);

    var area_turf = turf.area(polygon_turf);
    area_turf=area_turf/1000000
    console.log(JSON.stringify(formattedCoordinates))
  
    // Create polygon instance
    const polygon = L.polygon(formattedCoordinates, {
      color: '#2E90FA',
      weight: 4,
      fillOpacity: 0,
      fillColor: '#2E90FA',
      interactive: true
    });
  
    // Calculate area and other tooltip details (existing logic)
    let areaInSqKm = 0;
    try {
      // Convert coordinates to tuples of [lat, lng]
      const latLngs = coordinates.map(coord => [coord.lat, coord.lng]);
      
      // Calculate area using Leaflet's built-in functionality
      const latLngBounds = polygon.getBounds();
      const center = latLngBounds.getCenter();
      
      // Calculate approximate area using distance calculations
      let area = 0;
      for (let i = 0; i < latLngs.length; i++) {
        const j = (i + 1) % latLngs.length;
        const p1 = L.latLng(latLngs[i]);
        const p2 = L.latLng(latLngs[j]);
        const dx = p2.lng - p1.lng;
        const dy = p2.lat - p1.lat;
        area += dx * (2 * p1.lat + dy) / 2;
      }
      area = Math.abs(area * 111319.9 * 111319.9); // Convert to square meters (approximate)
      areaInSqKm = (area / 1000000).toFixed(2);
    } catch (error) {
      console.error('Error calculating area:', error);
      areaInSqKm = 'N/A';
    }
    
  
    // Calculate centroid
    const bounds = polygon.getBounds();
    const centroid = bounds.getCenter();
    const formattedCentroid = `[${centroid.lng.toFixed(4)},${centroid.lat.toFixed(4)}]`;
  
    // Prepare save button HTML
    let locationName = name;

if (!name) {
  const reverseGeocode = async () => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${centroid.lat}&lon=${centroid.lng}`);
      const data = await response.json();
      const { city, town, village, hamlet, county, state_district, state } = data.address;

const shortName = city || town || village || hamlet || county || state_district || state || 'Unnamed AOI';
      
      // Update popup after name is retrieved
      polygon.setPopupContent(popupTemplate(shortName));
    } catch (error) {
      console.warn('Reverse geocoding failed', error);
    }
  };
  reverseGeocode();
}

const popupTemplate = (locName) => `
  <div style="font-family: sans-serif; font-size: 12px; min-width: 200px; color: #A6B6CE; 
              background-color: rgba(22, 22, 22, 0.95); border-radius: 8px; 
              border: 1px solid #2E90FA; overflow: hidden;
              margin: 0; padding: 0;">
    <div style="padding: 10px; border-bottom: 1px solid rgba(46, 144, 250, 0.3);">
      <strong style="color: #fff;">AOI Details</strong>
    </div>
    <div style="padding: 10px;">
      <p style="margin: 0px 0; display: flex; justify-content: space-between;">
        <span>Name:</span>
        <span style="color: #fff;">${locName}</span>
      </p>
      <p style="margin: 5px 0; display: flex; justify-content: space-between;">
        <span>Area:</span>
        <span style="color: #fff;">${areaInSqKm} km²</span>
      </p>
      <p style="margin: 5px 0; display: flex; justify-content: space-between;">
        <span>Centroid:</span>
        <span style="color: #fff;">${formattedCentroid}</span>
      </p>
      <button onclick="window.saveAOI && window.saveAOI()" 
              style="background-color: #2E90FA; color: white; border: none; border-radius: 4px; 
                     padding: 6px 12px; margin-top: 8px; cursor: pointer; width: 100%;
                     font-size: 12px; font-weight: 500;">
        Save AOI
      </button>
    </div>
  </div>
`;

const popupContent = popupTemplate(locationName);

  // Bind popup to the polygon
  polygon.bindPopup(popupContent, {
    maxWidth: 300,
    className: 'custom-popup',
    closeButton: true
  });

  // Disable popup when measurement is active
  polygon.off('click'); // Remove any existing click handlers
  polygon.on('click', (e) => {
    if (drawModeRef.current === 'measure') {
      e.originalEvent.stopPropagation();
      e.originalEvent.preventDefault();
      return false;
    }
    polygon.openPopup();
  });

  
  // Add to map
  polygon.addTo(map);

  return polygon;
};

useEffect(() => {
  // Set up global save function
  window.saveAOI = handleSaveAOI;

  // Clean up
  return () => {
    delete window.saveAOI;
  };
}, [handleSaveAOI]);

  // Update the map initialization useEffect
// Update the map initialization useEffect
useEffect(() => {
  let isMounted = true;
  
  if (!mapContainerRef.current || map) return;

  const satelliteLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}');
  const streetLayer = L.tileLayer('http://mt0.google.com/vt/lyrs=r&hl=en&x={x}&y={y}&z={z}&gl=IN');
  // const terrainLayer = L.tileLayer('https://tiles.stadiamaps.com/tiles/stamen_terrain/{z}/{x}/{y}{r}.png');

  const layers = { 'Satellite': satelliteLayer, 'Streets': streetLayer};

  const mapInstance = L.map(mapContainerRef.current, {
    layers: [satelliteLayer],
    center: [20, 0],
    zoom: 2.5,
    minZoom: 2.5,
    maxZoom: 18,
    zoomDelta: 0.25,
    zoomSnap: 0.5,
    maxBounds: [
        [-90, -180],  // Southwest corner
        [90, 310]     // Northeast corner
    ],
    maxBoundsViscosity: 1.0,  // How "solid" the bounds are (1 = cannot escape bounds)
    worldCopyJump: false,      // When panning, jump to the other side of the map
    zoomControl: false,
    attributionControl: false
  });

  mapInstance.whenReady(() => {
    if (!isMounted) return;
    
    setMap(mapInstance);
    setBaseLayers(layers);

    // Track when global draw mode is toggled (e.g., user selects rectangle/polygon tool)
    mapInstance.on('pm:globaldrawmodetoggled', (e) => {
      console.log(`✏️ Global draw mode toggled: ${e.enabled ? 'enabled' : 'disabled'}`, e.shape || '');
      isDrawingRef.current = !!e.enabled;
      setIsDrawing(!!e.enabled);
    });

    // Track when drawing starts
    mapInstance.on('pm:drawstart', () => {
      console.log("🎨 Drawing started - preventing search triggers");
      isDrawingRef.current = true;
      setIsDrawing(true);
    });

    // Track when drawing ends (but before pm:create)
    mapInstance.on('pm:drawend', () => {
      console.log("✅ Drawing ended");
      // Keep flag true until pm:create fires
      isDrawingRef.current = true;
      setIsDrawing(true);
    });

    // Handle drawing cancellation
    mapInstance.on('pm:remove', () => {
      console.log("❌ Drawing cancelled/removed - resetting flag");
      isDrawingRef.current = false;
      setIsDrawing(false);
    });

    // Handle when drawing mode is disabled
    mapInstance.on('pm:disable', () => {
      console.log("🛑 Drawing disabled - resetting flag");
      isDrawingRef.current = false;
      setIsDrawing(false);
    });

    mapInstance.on('pm:create', (e) => {
      // Drawing is complete, allow searches again
      isDrawingRef.current = false;
      setIsDrawing(false);
      console.log("✅ Drawing complete (pm:create) - search allowed");
      
      // Remove existing polygons
      mapInstance.eachLayer((layer) => {
        if (layer instanceof L.Polygon && !(layer instanceof L.Rectangle)) {
          mapInstance.removeLayer(layer);
        }
      });

      const coordinates = e.layer.getLatLngs()[0].map(latLng => ({
        lat: latLng.lat,
        lng: latLng.lng
      }));

      // Remove the automatically added layer
      mapInstance.removeLayer(e.layer);

      // Create new polygon with tooltip
      console.log("coordinates from map useeffect:",JSON.stringify(coordinates))
      const polygon = createPolygonWithTooltip(coordinates, mapInstance);
      setActiveAOIPolygon(polygon);
      
      setDrawnCoordinates(coordinates);
      
      // Trigger search/feasibility after drawing is complete
      if (dateRange) {
        // If on tasking tab, trigger feasibility checks instead of search
        if (activeTab === 'task') {
          console.log("🔄 [ExplorePage] Drawing complete on tasking tab - clearing selections and triggering feasibility checks");
          // Clear selections when new AOI is drawn (on tasking tab)
          if (clearSelectionsRef.current) {
            console.log("🧹 [ExplorePage] Clearing selections - new AOI drawn");
            clearSelectionsRef.current();
          }
          // Use a small delay to ensure state is updated
          setTimeout(() => {
            if (feasibilityTriggerRef.current) {
              console.log("🚀 [ExplorePage] Calling feasibility trigger");
              feasibilityTriggerRef.current();
            } else {
              console.warn("⚠️ [ExplorePage] Feasibility trigger not available yet");
            }
          }, 200); // Increased delay to ensure state is ready
        } else {
          console.log("here4 - triggering search after drawing complete");
        handleSearchRequest(coordinates, dateRange);
        }
      } else {
        console.log("⏸️ [ExplorePage] No dateRange - skipping search/feasibility");
      }
    });

//     // Sample heatmap data [lat, lng, intensity]
//     const heatPoints = [
//       // Bangalore city center
//       [12.9716, 77.5946, 0.9],
    
//       // Nearby neighborhoods
//       [13.0350, 77.5970, 0.7],  // Hebbal
//       [12.9250, 77.5938, 0.6],  // Jayanagar
//       [12.9585, 77.7012, 0.4],  // KR Puram
//       [12.8673, 77.6022, 0.5],  // Electronic City
//       [13.0080, 77.5530, 0.8],  // Yeshwanthpur
    
//       // Spread across surrounding towns
//       [13.3392, 77.1135, 0.3],  // Doddaballapura
//       [12.7091, 77.2812, 0.2],  // Kanakapura
//       [13.0827, 78.0081, 0.6],  // Kolar
//       [12.5239, 77.8320, 0.5],  // Malur
//       [13.1194, 77.9798, 0.4],  // Sidlaghatta
//       [12.9649, 76.9572, 0.3],  // Ramanagara
    
//       // Wide region scatter
//       [12.8500, 77.7500, 0.3],
//       [13.0000, 77.8500, 0.5],
//       [12.9500, 77.4500, 0.2],
//       [13.1000, 77.6500, 0.6],
//       [12.7500, 77.6500, 0.4],
//       [13.2000, 77.3000, 0.2],
//       [12.8500, 77.2000, 0.3],
//       [13.2500, 77.9000, 0.7]
//     ];
    

// const heatLayer = L.heatLayer(heatPoints, {
//   radius: 25,
//   blur: 15,
//   maxZoom: 10,
//   gradient: {
//     0.4: 'blue',
//     0.6: 'lime',
//     0.9: 'red'
//   }
// }).addTo(mapInstance);

    
    // Add listener for direct polygon edits
    mapInstance.on('pm:edit', (e) => {
      if (e.layer && e.layer.getLatLngs) {
        // Get the updated coordinates from the edited polygon
        const latLngs = e.layer.getLatLngs()[0];
        const updatedCoordinates = latLngs.map(latLng => ({
          lat: latLng.lat,
          lng: latLng.lng
        }));
        
        // Update drawnCoordinates state
        setDrawnCoordinates(updatedCoordinates);
        
        console.log('AOI edited directly, updated coordinates:', updatedCoordinates);
      }
    });
    
    // When global removal mode is active and polygon is removed
    mapInstance.on('pm:remove', (e) => {
      if (activeAOIPolygon && e.layer === activeAOIPolygon) {
        handleAOIDelete();
      }
    });
    
    // Enhanced pm:globaleditmodetoggled handler
    mapInstance.on('pm:globaleditmodetoggled', (e) => {
      // When edit mode is toggled off (finished editing)
      if (!e.enabled) {
        // Find the AOI polygon and get its updated coordinates
        let updatedCoordinates = null;
        mapInstance.eachLayer((layer) => {
          // Check if it's our AOI polygon
          if (layer instanceof L.Polygon && 
              layer.options && 
              layer.options.color === '#2E90FA' && 
              layer.options.fillOpacity === 0) {
            
            // Get the updated coordinates
            const latLngs = layer.getLatLngs()[0];
            updatedCoordinates = latLngs.map(latLng => ({
              lat: latLng.lat,
              lng: latLng.lng
            }));
          }
        });
        
        // If we found updated coordinates, update state and trigger search
        if (updatedCoordinates) {
          setDrawnCoordinates(updatedCoordinates);
          
          // If we have a date range, perform a search
          if (dateRange ) {
            console.log("here5")
            handleSearchRequest(updatedCoordinates, dateRange);
          }
          console.log('Edit mode disabled, search triggered with updated coordinates');
        }
      }
    });

    // COMMENTED OUT: Auto-location detection disabled
    // if ("geolocation" in navigator) {
    //   navigator.geolocation.getCurrentPosition(
    //     (position) => {
    //       if (!isMounted){
    //         return;}
    //       const { latitude, longitude } = position.coords;
    
    //       if (mapInstance && mapInstance._container) {
    //         mapInstance.flyTo([latitude, longitude], 12, {
    //           animate: true,
    //           duration: 2.5,
    //           easeLinearity: 0.1,
    //           noMoveStart: false
    //         });
    
    //         const center = turf.point([longitude, latitude]);
    //         const buffered = turf.buffer(center, 10, { units: 'kilometers' });
    //         const bbox = turf.bbox(buffered);
    //         const squareAOI = turf.bboxPolygon(bbox);
    //         const coords = squareAOI.geometry.coordinates[0].map(coord => ({
    //           lat: coord[1],
    //           lng: coord[0]
    //         }));
    
    //         mapInstance.eachLayer((layer) => {
    //           if (layer instanceof L.Polygon && !(layer instanceof L.Rectangle)) {
    //             mapInstance.removeLayer(layer);
    //           }
    //         });
    
    //         setTimeout(() => {
    //           const polygon = createPolygonWithTooltip(coords, mapInstance);
    //           setActiveAOIPolygon(polygon);
    //           setDrawnCoordinates(coords);
    //         }, 2500);
    //       }
    //     },
    //     () => {
    //       if (!isMounted){
    //         return;}
    //       // Fallback to Bangalore
    //       const latitude = 12.9716;
    //       const longitude = 77.5946;
    
    //       if (mapInstance && mapInstance._container) {
    //         mapInstance.flyTo([latitude, longitude], 12, {
    //           animate: true,
    //           duration: 2.5,
    //           easeLinearity: 0.1,
    //           noMoveStart: false
    //         });
    
    //         const center = turf.point([longitude, latitude]);
    //         const buffered = turf.buffer(center, 10, { units: 'kilometers' });
    //         const bbox = turf.bbox(buffered);
    //         const squareAOI = turf.bboxPolygon(bbox);
    //         const coords = squareAOI.geometry.coordinates[0].map(coord => ({
    //           lat: coord[1],
    //           lng: coord[0]
    //         }));
    
    //         mapInstance.eachLayer((layer) => {
    //           if (layer instanceof L.Polygon && !(layer instanceof L.Rectangle)) {
    //             mapInstance.removeLayer(layer);
    //           }
    //         });
    
    //         setTimeout(() => {
    //           const polygon = createPolygonWithTooltip(coords, mapInstance);
    //           setActiveAOIPolygon(polygon);
    //           setDrawnCoordinates(coords);
    //         }, 2500);
    //       }
    //     }
    //   );
    // }

    // Default area based on user role
    if (mapInstance && mapInstance._container) {
      let defaultCoordinates;
      let dateRange;
      
      // Check if user is Hackathon (check email from token)
      const token = localStorage.getItem('token');
      let isHackathon = false;
      try {
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          console.log('Token payload:', payload); // Debug log
          // Check if email contains 'hackathon' or if role is 'hackathon'
          isHackathon = (payload.sub && payload.sub.toLowerCase().includes('hackathon')) || 
                       (payload.role && payload.role.toLowerCase() === 'hackathon');
          console.log('Is Hackathon user:', isHackathon); // Debug log
        }
      } catch (e) {
        console.log('Token parsing error:', e); // Debug log
        // If token parsing fails, default to non-hackathon
      }
      
      if (isHackathon) {
        // Hackathon users: Salzburg region
        defaultCoordinates = [
          { lat: 47.8, lng: 12.9 },  // Northwest
          { lat: 47.8, lng: 13.2 },  // Northeast  
          { lat: 47.6, lng: 13.2 },  // Southeast
          { lat: 47.6, lng: 12.9 }   // Southwest
        ];
        
        // Hackathon users get April 2024 range
        dateRange = {
          startDate: '2024-04-06',
          endDate: '2024-04-08'
        };
        console.log('Setting Hackathon date range:', dateRange);
      } else {
        // Non-Hackathon users: Default AOI region
        defaultCoordinates = [
          { lat: 24.8618885, lng: 66.9495044 },
          { lat: 24.7892865, lng: 66.9498477 },
          { lat: 24.7892865, lng: 67.0234904 },
          { lat: 24.8631345, lng: 67.0231471 }
        ];
        
        // Non-Hackathon users get last 30 days
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        
        dateRange = {
          startDate: thirtyDaysAgo.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0]
        };
        console.log('Setting non-Hackathon date range:', dateRange);
      }
      
      // Clear any existing AOI
      if (activeAOIPolygon && mapInstance) {
        mapInstance.removeLayer(activeAOIPolygon);
      }
      
      // Set the new coordinates
      setDrawnCoordinates(defaultCoordinates);
      
      // Create new polygon with tooltip
      const polygon = createPolygonWithTooltip(defaultCoordinates, mapInstance, isHackathon ? "Salzburg Region" : "Default Region");
      setActiveAOIPolygon(polygon);
      
      // Fit the map view to the polygon
      mapInstance.fitBounds(polygon.getBounds());
      
      // Set the date range
      setDateRange(dateRange);
      
      // Trigger search immediately when map is ready
        if (mapInstance && mapInstance._container && isMounted) {
          console.log(`Triggering automatic ${isHackathon ? 'Salzburg' : 'Default'} search...`);
          handleSearchRequest(defaultCoordinates, dateRange);
        }
    }
      
    
    console.log("returning");
  });

  return () => {
    isMounted = false;
    if (mapInstance) {
      mapInstance.remove();
    }
  };
}, []);

useEffect(() => {
  if (!map) return;
  const activeImg = visibleImage || hoveredImage;
  
  if (previewLayer) {
    map.removeLayer(previewLayer);
    setPreviewLayer(null);
  }

  if (!activeImg?.coordinates) return;

  const polygonCoords = activeImg.coordinates[0].map(coord => [coord[1], coord[0]]);
  
  if (visibleImage) {
    const imageOverlay = L.imageOverlay(
      activeImg.previewUrl,
      L.latLngBounds(polygonCoords),
      { opacity: 0, interactive: true }
    ).addTo(map);
    setPreviewLayer(imageOverlay);
    map.fitBounds(polygonCoords);
  }
  else if (hoveredImage) {
    const highlightPolygon = L.polygon(polygonCoords, {
      color: 'yellow',
      weight: 2,
      fillOpacity: 0.1
    }).addTo(map);
    setPreviewLayer(highlightPolygon);
  }
}, [map, visibleImage, hoveredImage]);

const handleImageHover = (image) => {
  if (!visibleImage) {
    setHoveredImage(image);
  }
};
const show = (val) => val !== undefined && val !== null && !isNaN(val);

// Modify the handleImageSelect function to add tooltips to selected image polygons
const handleImageSelect = (image) => {
  if (!map) return;

  const isCurrentlySelected = selectedImages.some(img => img.id === image.id);

  if (isCurrentlySelected) {
    setSelectedImages(prev => prev.filter(img => img.id !== image.id));

    const overlayKey = `overlay_${image.id}`;
    if (map[overlayKey]) {
      map.removeLayer(map[overlayKey]);
      delete map[overlayKey];
    }

    if (selectedPolygons[image.id]) {
      map.removeLayer(selectedPolygons[image.id]);
      const updatedPolygons = { ...selectedPolygons };
      delete updatedPolygons[image.id];
      setSelectedPolygons(updatedPolygons);
    }
  } else {
    setSelectedImages(prev => [...prev, image]);

    if (image.coordinates) {
      const polygonCoords = image.coordinates[0].map(coord => [coord[1], coord[0]]);
      const hasIS18021 = image.id?.includes('IS18021');

      const tooltipContent = hasIS18021
        ? `
        <div style="font-family: sans-serif; font-size: 12px; min-width: 200px; color: #A6B6CE; 
                    background-color: rgba(22, 22, 22, 0.95); border-radius: 8px; 
                    border: 1px solid #2E90FA; overflow: hidden;
                    margin: 0; padding: 0;">
          <div style="padding: 10px; border-bottom: 1px solid rgba(46, 144, 250, 0.3);">
            <div style="display: flex; align-items: center; gap: 6px;">
  <div style="width: 10px; height: 10px; background-color: ${image.satelliteColor}; border-radius: 2px;"></div>
  <strong style="color: #fff;">${image.satellite || 'Unknown'}</strong>
</div>

          </div>
          <div style="padding: 10px;">
            <p style="margin: 0 0 5px 0; display: flex; justify-content: space-between; align-items: center;">
              <span>Date:</span>
              <span style="color: #fff; display: flex; align-items: center;">${getCalendarIconSVG(12, '#fff')} ${image.date || 'N/A'}</span>
            </p>
            <p style="margin: 5px 0; display: flex; justify-content: space-between; align-items: center;">
              <span>Coverage:</span>
              <span style="color: #fff; display: flex; align-items: center;">${getCoverageIconSVG(12, '#fff')} ${image.coveragePercentage || 'N/A'}%</span>
            </p>
          </div>
        </div>
        `
        : `
        <div style="font-family: sans-serif; font-size: 12px; min-width: 200px; color: #A6B6CE; 
                    background-color: rgba(22, 22, 22, 0.95); border-radius: 8px; 
                    border: 1px solid #2E90FA; overflow: hidden;
                    margin: 0; padding: 0;">
          <div style="padding: 10px; border-bottom: 1px solid rgba(46, 144, 250, 0.3);">
            <div style="display: flex; align-items: center; gap: 6px;">
  <div style="width: 10px; height: 10px; background-color: ${image.satelliteColor}; border-radius: 2px;"></div>
  <strong style="color: #fff;">${image.satellite || 'Unknown'}</strong>
</div>

          </div>
          <div style="padding: 10px;">
            ${image.date ? `
              <p style="margin: 0 0 5px 0; display: flex; justify-content: space-between; align-items: center;">
              <span>Date:</span>
                <span style="color: #fff; display: flex; align-items: center;">${getCalendarIconSVG(12, '#fff')} ${image.date}</span>
            </p>
            ` : ''}
            ${show(image.coveragePercentage) ? `
              <p style="margin: 5px 0; display: flex; justify-content: space-between; align-items: center;">
                <span>Coverage:</span>
                <span style="color: #fff; display: flex; align-items: center;">${getCoverageIconSVG(12, '#fff')} ${Number(image.coveragePercentage).toFixed(1)}%</span>
            </p>
            ` : ''}
            ${show(parseFloat(image.coverage)) ? `
              <p style="margin: 5px 0; display: flex; justify-content: space-between; align-items: center;">
              <span>Cloud Cover:</span>
                <span style="color: #fff; display: flex; align-items: center;">${getCloudIconSVG(12, '#fff')} ${parseFloat(image.coverage).toFixed(1)}%</span>
            </p>
            ` : ''}
            ${show(image.off_nadir_angle) ? `
              <p style="margin: 5px 0; display: flex; justify-content: space-between; align-items: center;">
              <span>Off-Nadir:</span>
                <span style="color: #fff; display: flex; align-items: center;">${getOffNadirIconSVG(12, '#fff')} ${Number(image.off_nadir_angle).toFixed(1)}°</span>
            </p>
            ` : ''}
            ${show(image.angle) ? `
              <p style="margin: 5px 0; display: flex; justify-content: space-between; align-items: center;">
                <span>Sun Elevation:</span>
                <span style="color: #fff; display: flex; align-items: center;">${getSunElevationIconSVG(12, '#fff')} ${Number(image.angle).toFixed(1)}°</span>
            </p>
            ` : ''}
          </div>

        </div>
        `;

      // Make aerial collection polygons transparent (test, hackathon, and SIFT)
        const isAerialCollection = image.satellite === 'Kaleideo_Aerial_Hackathon' || image.satellite === 'kaleideo-aerial-test' || image.satellite === 'SIFT_RGB_TEST' || image.satellite === 'keo_aerial_V6';
      
      const polygon = L.polygon(polygonCoords, {
        color: image.satelliteColor,
        weight: isAerialCollection ? 0 : 2, // Make border invisible for aerial collections
        fillOpacity: 0,
        opacity: isAerialCollection ? 0 : 1 // Make entire polygon transparent for aerial collections
      });

      // For keo_aerial_V6, use popup instead of tooltip
      if (image.satellite === 'keo_aerial_V6') {
        // Add custom CSS to remove white borders
        if (!document.getElementById('keo-aerial-popup-styles')) {
          const style = document.createElement('style');
          style.id = 'keo-aerial-popup-styles';
          style.textContent = `
            .keo-aerial-popup .leaflet-popup-content-wrapper {
              background: transparent !important;
              border: none !important;
              box-shadow: none !important;
              padding: 0 !important;
            }
            .keo-aerial-popup .leaflet-popup-content {
              margin: 0 !important;
              padding: 0 !important;
            }
            .keo-aerial-popup .leaflet-popup-tip {
              background: rgba(22, 22, 22, 0.95) !important;
              border: 1px solid #2E90FA !important;
            }
          `;
          document.head.appendChild(style);
        }

        polygon.bindPopup(`
          <div style="font-family: sans-serif; font-size: 12px; min-width: 200px; color: #A6B6CE; 
                      background-color: rgba(22, 22, 22, 0.95); border-radius: 8px; 
                      border: 1px solid #2E90FA; overflow: hidden;
                      margin: 0; padding: 0;">
            <div style="padding: 10px; border-bottom: 1px solid rgba(46, 144, 250, 0.3);">
              <strong style="color: #fff;">Image Details</strong>
            </div>
            <div style="padding: 10px;">
              <p style="margin: 0px 0; display: flex; justify-content: space-between;">
                <span>ID:</span>
                <span style="color: #fff;">${image.id || 'N/A'}</span>
              </p>
            </div>
          </div>
        `, {
          maxWidth: 250,
          className: 'keo-aerial-popup',
          closeButton: true
        });
      } else {
        polygon.bindTooltip(tooltipContent, {
          sticky: true,
          direction: 'top',
          offset: [0, -10],
          opacity: 0.9,
          className: 'image-tooltip'
        });
      }

      const isVisible = satelliteVisibility[image.satellite] !== false;

      const imageOverlay = L.imageOverlay(
        image.previewUrl,
        L.latLngBounds(polygonCoords),
        { opacity: 1, interactive: true }
      );
      map[`overlay_${image.id}`] = imageOverlay;

      if (isVisible) {
        polygon.addTo(map);
        imageOverlay.addTo(map);
        map.fitBounds(polygonCoords);
      }

      setSelectedPolygons(prev => ({
        ...prev,
        [image.id]: polygon
      }));
    }
  }
};

const getSelectedProduct = () => {
  // For tasking flows, use the selected constellation from tasking data
  if (activeTab === 'task' && taskingData?.constellationName) {
    return taskingData.constellationName;
  }

  if (selectedImages && selectedImages.length > 0) {
    // Extract product ID from the first selected image
    // This assumes all selected images are from the same product/satellite
    return selectedImages[0].product_id || selectedImages[0].satellite;
  }
  return null;
};


const handleImageVisibilityToggle = (image) => {
  if (visibleImage && visibleImage.id === image.id) {
    setVisibleImage(null);
  } else {
    setVisibleImage(image);
  }
};

// Centralized date range change handler
const handleDateRangeChange = (newDateRange) => {
  // Clear map overlays when date range changes
  if (map) {
    // Remove preview layer if it exists
    if (previewLayer) {
      map.removeLayer(previewLayer);
      setPreviewLayer(null);
    }
    
    // Remove each selected image/footprint polygon
    Object.values(selectedPolygons).forEach((layer) => {
      if (map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    });
    
    // Clear all image overlays that were added directly to map
    Object.keys(map).forEach(key => {
      if (key.startsWith('overlay_') && map[key] instanceof L.ImageOverlay) {
        if (map.hasLayer(map[key])) {
          map.removeLayer(map[key]);
        }
        delete map[key];
      }
    });
    
    // Clear all state related to selected imagery
    setSelectedPolygons({});
    setSelectedImages([]);
    setVisibleImage(null);
    setHoveredImage(null);
  }
  
  // Store dates in the ref if we're in catalog tab
  if (activeTab === 'catalog') {
    catalogDatesRef.current = newDateRange;
  }
  
  // Update centralized date range state
  setDateRange(newDateRange);
  
  // Clear the default dates flag if user is manually changing dates (user-initiated action should always trigger search)
  // This ensures manual date changes always work, even if default dates were just set
  const wasSettingDefaultDates = isSettingDefaultDatesRef.current;
  if (wasSettingDefaultDates) {
    console.log("🔓 Clearing default dates flag - user manually changed date range");
    isSettingDefaultDatesRef.current = false;
  }
  
  // Also clear the restoring flag if it's set (user action should override restoration)
  if (isRestoringCatalogStateRef.current) {
    console.log("🔓 Clearing restore flag - user manually changed date range");
    isRestoringCatalogStateRef.current = false;
  }
  
  // Trigger a new search with the new date range (only for catalog tab)
  // handleSearchRequest has its own duplicate prevention, so we don't need !loading check
  // IMPORTANT: Manual date changes should ALWAYS trigger search, regardless of flags
  // Use ref as fallback if activeTab state is empty (timing issue)
  // Also infer catalog mode if date range is NOT tasking dates
  const currentActiveTab = activeTab || activeTabRef.current;
  const isTaskingDates = isTaskingDateRange(newDateRange.startDate, newDateRange.endDate);
  // CRITICAL: Only trigger search if we're explicitly on catalog tab, NOT on tasking tab
  // If activeTab is 'task', never trigger search API
  const isCatalogTab = currentActiveTab === 'catalog' && currentActiveTab !== 'task';
  
  console.log("🔍 handleDateRangeChange - checking conditions:", {
    activeTab,
    activeTabRef: activeTabRef.current,
    currentActiveTab,
    isTaskingDates,
    isCatalog: isCatalogTab,
    isTaskingTab: currentActiveTab === 'task',
    hasCoordinates: !!drawnCoordinates,
    coordinatesLength: drawnCoordinates?.length,
    hasStartDate: !!newDateRange.startDate,
    hasEndDate: !!newDateRange.endDate,
    newDateRange,
    wasSettingDefaultDates,
    wasRestoring: isRestoringCatalogStateRef.current
  });
  
  // If on tasking tab, trigger feasibility checks instead of search
  if (currentActiveTab === 'task' && drawnCoordinates && newDateRange.startDate && newDateRange.endDate) {
    console.log("🔄 [TASKING TAB] Date range changed - clearing selections and triggering feasibility checks");
    
    // CRITICAL: Clear stored tasking state when dates change to prevent restoring old results
    // Check if dates actually changed (not just initial load)
    if (taskingStateRef.current.hasState && taskingStateRef.current.dateRange) {
      const datesChanged = taskingStateRef.current.dateRange.startDate !== newDateRange.startDate ||
                          taskingStateRef.current.dateRange.endDate !== newDateRange.endDate;
      if (datesChanged) {
        console.log("🗑️ [ExplorePage] Clearing stored tasking state - dates changed", {
          oldDates: taskingStateRef.current.dateRange,
          newDates: newDateRange,
        });
        // Clear ALL stored state including dateRange to ensure old results aren't restored
        taskingStateRef.current.hasState = false;
        taskingStateRef.current.dateRange = null; // CRITICAL: Clear dateRange so restoration won't match
        taskingStateRef.current.taskingDetails = {};
        taskingStateRef.current.constellations = [];
        taskingStateRef.current.stripSelections = {};
        taskingStateRef.current.visibleConstellations = {};
        taskingStateRef.current.checkedConstellations = {};
        
        // CRITICAL: Clear taskingDetails in TaskingConstellations IMMEDIATELY to prevent storing old results
        if (clearTaskingResultsRef.current) {
          console.log("🗑️ [ExplorePage] Clearing taskingDetails in TaskingConstellations - dates changed");
          clearTaskingResultsRef.current();
        }
      }
    } else if (!taskingStateRef.current.hasState) {
      // Also clear dateRange if hasState is already false (first time or after previous clear)
      taskingStateRef.current.dateRange = null;
    }
    
    // Clear selections when date range changes
    if (clearSelectionsRef.current) {
      console.log("🧹 [ExplorePage] Clearing selections - date range changed");
      clearSelectionsRef.current();
    }
    // Trigger feasibility checks
    setTimeout(() => {
      if (feasibilityTriggerRef.current) {
        console.log("🚀 [ExplorePage] Calling feasibility trigger after date range change");
        feasibilityTriggerRef.current();
      } else {
        console.warn("⚠️ [ExplorePage] Feasibility trigger not available yet");
      }
    }, 200);
  } else if (isCatalogTab && drawnCoordinates && newDateRange.startDate && newDateRange.endDate) {
    console.log("✅ [CATALOG TAB] Triggering search from date change (user-initiated)", { 
      newDateRange, 
      drawnCoordinates,
      isSearching: isSearchingRef.current,
      loading
    });
    // Direct call - bypasses all flags since this is user-initiated
    // Force search even if previous search is stuck (user action should always work)
    handleSearchRequest(drawnCoordinates, newDateRange);
  } else {
    console.log("❌ Skipping search trigger:", { 
      activeTab, 
      currentActiveTab,
      isCatalog: isCatalogTab,
      isTaskingTab: currentActiveTab === 'task',
      hasCoordinates: !!drawnCoordinates, 
      hasDates: !!(newDateRange.startDate && newDateRange.endDate),
      reason: !currentActiveTab ? 'no activeTab' : 
              currentActiveTab === 'task' ? 'tasking tab - use feasibility instead' :
              currentActiveTab !== 'catalog' ? 'not catalog tab' :
              !drawnCoordinates ? 'no coordinates' :
              !newDateRange.startDate || !newDateRange.endDate ? 'missing dates' : 'unknown'
    });
  }
}

// Update this useEffect to properly clear all preview images and overlays
useEffect(() => {
if (!map) return; // Don't proceed if map isn't initialized

// Skip clearing if we're restoring catalog state (to preserve restored state)
if (isRestoringCatalogStateRef.current) {
  console.log("Skipping layer clearing - restoring catalog state");
  return;
}

// Remove preview layer if it exists
if (previewLayer) {
  map.removeLayer(previewLayer);
  setPreviewLayer(null);
}

// Remove each selected image/footprint polygon
Object.values(selectedPolygons).forEach((layer) => {
  map.removeLayer(layer);
});

// Also remove any image overlays that might have been added directly to map
// These have keys like "overlay_imageId" stored on the map object
Object.keys(map).forEach(key => {
  if (key.startsWith('overlay_') && map[key] instanceof L.ImageOverlay) {
    map.removeLayer(map[key]);
    delete map[key];
  }
});

// Clear the selected layers state (this does NOT include AOI polygon)
// Only clear if we're actually switching tabs (not just restoring)
if (activeTab && previousActiveTabRef.current && previousActiveTabRef.current !== activeTab) {
setSelectedPolygons({});
setSelectedImages([]);

// Clear any visible or hovered image state
setVisibleImage(null);
setHoveredImage(null);
}
}, [drawnCoordinates, activeTab, map]); // Removed dateRange and loading to prevent loops

// Separate useEffect for triggering catalog searches
useEffect(() => {
  // Skip if we're restoring catalog state (to prevent unnecessary search)
  if (isRestoringCatalogStateRef.current) {
    console.log("Skipping search - restoring catalog state");
    return;
  }
  
  // Skip if we're setting default dates for the first time (to prevent auto-trigger on initial catalog visit)
  if (isSettingDefaultDatesRef.current) {
    console.log("Skipping search - setting default dates for first time");
    return;
  }
  
  // Skip if user is actively drawing (wait for drawing to complete)
  if (isDrawingRef.current) {
    console.log("Skipping search - user is actively drawing");
    return;
  }
  
  // CRITICAL: Check if current dates match stored catalog dates - if so, don't trigger search
  // This prevents search when we're just restoring catalog dates (not actually changing them)
  const storedCatalogDates = catalogDatesRef.current;
  const hasStoredCatalogDates = storedCatalogDates && storedCatalogDates.startDate && storedCatalogDates.endDate;
  
  // CRITICAL: If we're on catalog tab and have stored catalog dates but current dates don't match,
  // we're in the middle of restoration - skip search to avoid using wrong dates
  if (activeTab === 'catalog' && hasStoredCatalogDates && dateRange && dateRange.startDate && dateRange.endDate) {
    const datesMatch = dateRange.startDate === storedCatalogDates.startDate && 
                       dateRange.endDate === storedCatalogDates.endDate;
    
    // If dates don't match stored catalog dates, we're restoring - skip search
    if (!datesMatch) {
      console.log("Skipping search - dates don't match stored catalog dates (restoring in progress)", {
        currentDates: dateRange,
        storedCatalogDates,
        activeTab
      });
      return;
    }
    
    // Also check if AOI matches stored catalog AOI
    const storedCoordinates = catalogStateRef.current.drawnCoordinates;
    const coordinatesMatch = storedCoordinates && drawnCoordinates && 
      JSON.stringify(storedCoordinates) === JSON.stringify(drawnCoordinates);
    
    // If both dates and coordinates match stored catalog state, don't trigger search
    // (we're just restoring, not actually changing anything)
    if (datesMatch && coordinatesMatch && catalogStateRef.current.hasState) {
      console.log("Skipping search - dates and AOI match stored catalog state (just restoring, no actual change)", {
        datesMatch,
        coordinatesMatch,
        hasStoredState: catalogStateRef.current.hasState
      });
      return;
    }
  }
  
  // Only trigger search for catalog tab when we have all required data and not loading
  if (activeTab === 'catalog' && drawnCoordinates && dateRange && dateRange.startDate && dateRange.endDate && !loading && map) {
    // Verify that the date range is actually catalog dates (not tasking dates)
    const isTaskingDates = isTaskingDateRange(dateRange.startDate, dateRange.endDate);
    
    if (!isTaskingDates) {
      console.log("AOI or date range changed - triggering catalog search", {
        hasCoordinates: !!drawnCoordinates,
        coordinatesLength: drawnCoordinates?.length,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        activeTab,
        loading
      });
      handleSearchRequest(drawnCoordinates, dateRange);
    } else {
      console.log("Skipping search - date range is still tasking dates, waiting for catalog dates to be set");
    }
}
}, [drawnCoordinates, dateRange, activeTab, map]); // Removed loading from dependencies to prevent loop

// Keep activeTabRef in sync with activeTab state
useEffect(() => {
  if (activeTab) {
    activeTabRef.current = activeTab;
  }
}, [activeTab]);

// Keep catalog state snapshot up to date whenever catalog dates change
useEffect(() => {
  if (
    activeTab === 'catalog' &&
    dateRange &&
    dateRange.startDate &&
    dateRange.endDate &&
    !isRestoringCatalogStateRef.current &&
    !isSettingDefaultDatesRef.current
  ) {
    const isTaskingDates = isTaskingDateRange(dateRange.startDate, dateRange.endDate);
    if (!isTaskingDates) {
      storeCatalogState('catalog-date-change');
    } else {
      console.log('📂 Skipping catalog state save - dates look like tasking preset');
    }
  }
}, [activeTab, dateRange?.startDate, dateRange?.endDate, storeCatalogState]);

// Save catalog/tasking state when switching away from tabs
useEffect(() => {
  const previousTab = previousActiveTabRef.current;
  
  // If we're switching from catalog to another tab, save the catalog state
  if (previousTab === 'catalog' && activeTab && activeTab !== 'catalog') {
    storeCatalogState(`effect-${activeTab}`);
  }
  
  // Tasking state is stored in ref via onTaskingStateReady callback - nothing to do here
  
  // Update the previous tab reference after processing
  previousActiveTabRef.current = activeTab;
}, [activeTab, storeCatalogState, dateRange, drawnCoordinates, taskingData]);

// Set default date ranges when switching tabs - centralized state management
useEffect(() => {
  if (!activeTab) return; // Don't set dates if no tab is active
  
  if (activeTab === 'task') {
    // Get the tasking date range (today to 7 days ahead) - for default only
    const taskingDates = getTaskingDateRange();
    
    // Check if we can restore stored tasking state
    const stored = taskingStateRef.current;
    if (stored.hasState && stored.dateRange && stored.drawnCoordinates && drawnCoordinates) {
      // Compare AOI - if it matches, we can restore (dates will be restored from stored state)
      const coordsMatch = JSON.stringify(stored.drawnCoordinates) === JSON.stringify(drawnCoordinates);
      
      // Compare dates - check if stored dates match current dateRange (if any)
      const datesMatch = dateRange && 
        dateRange.startDate === stored.dateRange.startDate && 
        dateRange.endDate === stored.dateRange.endDate;
      
      // If coordinates match and we have stored state, restore it
      // We restore stored dates regardless of current dates (they might be catalog dates)
      if (coordsMatch && Object.keys(stored.taskingDetails).length > 0) {
        console.log("✅ Restoring tasking state - AOI matches", {
          storedDates: stored.dateRange,
          currentDates: dateRange,
          datesMatch,
          taskingDetailsCount: Object.keys(stored.taskingDetails).length,
        });
        isRestoringTaskingStateRef.current = true;
        
        // CRITICAL: Always restore stored dateRange (even if current dates are catalog dates)
        // This prevents the auto-trigger from firing with wrong dates
        if (!datesMatch) {
          console.log("📅 Restoring stored tasking dates:", stored.dateRange);
          setDateRange(stored.dateRange);
        }
        
        // Restore tasking data
        setTaskingData({
          taskingDetails: stored.taskingDetails,
          constellations: stored.constellations,
          checkedConstellations: stored.checkedConstellations,
          stripSelections: stored.stripSelections || {},
          visibleConstellations: stored.visibleConstellations || {},
        });
        
        setTimeout(() => {
          isRestoringTaskingStateRef.current = false;
        }, 500); // Increased delay to ensure auto-trigger doesn't fire
        
        return; // Don't set default dates if we're restoring state
      } else {
        // AOI changed - clear stored state
        console.log("🔄 Tasking state not restored - AOI changed", {
          coordsMatch,
          storedDates: stored.dateRange,
          currentDates: dateRange,
        });
        taskingStateRef.current.hasState = false;
      }
    }
    
    // No stored state or AOI changed - set default tasking dates if needed
    // BUT: Only set if we're not restoring (to prevent triggering feasibility during restoration)
    if (!isRestoringTaskingStateRef.current) {
      if (!dateRange || dateRange.startDate !== taskingDates.startDate || dateRange.endDate !== taskingDates.endDate) {
        setDateRange(taskingDates);
      }
    }
  } else if (activeTab === 'catalog') {
    // CRITICAL: Always restore catalog dates from catalogDatesRef FIRST
    // This prevents tasking dates from being used on catalog tab
    const storedCatalogDates = catalogDatesRef.current;
    const hasStoredCatalogDates = storedCatalogDates && storedCatalogDates.startDate && storedCatalogDates.endDate;
    const currentIsTaskingDates = dateRange && isTaskingDateRange(dateRange.startDate, dateRange.endDate);
    
    // Check if we have stored catalog state to restore (imagery, selections, etc.)
    // Check this FIRST before restoring dates, so we can detect AOI changes early
    let shouldRestoreCatalogState = false;
    let aoiChanged = false;
    
    if (catalogStateRef.current.hasState && catalogStateRef.current.dateRange) {
      const storedDateRange = catalogStateRef.current.dateRange;
      const storedCoordinates = catalogStateRef.current.drawnCoordinates;
      
      // Compare BOTH dates and AOI - both must match to restore
      const datesMatch = dateRange && 
        dateRange.startDate === storedDateRange.startDate && 
        dateRange.endDate === storedDateRange.endDate;
      const coordsMatch = storedCoordinates && drawnCoordinates && 
        JSON.stringify(storedCoordinates) === JSON.stringify(drawnCoordinates);
      
      // Only restore if BOTH dates AND AOI match
      if (datesMatch && coordsMatch) {
        shouldRestoreCatalogState = true;
      } else if (!coordsMatch) {
        // AOI changed - mark it so we can trigger search after date restoration
        aoiChanged = true;
        catalogStateRef.current.hasState = false;
      }
    }
    
    // If we have stored catalog dates, use them (even if current dates are tasking dates)
    if (hasStoredCatalogDates) {
      const datesMatch = dateRange && 
        dateRange.startDate === storedCatalogDates.startDate && 
        dateRange.endDate === storedCatalogDates.endDate;
      
      if (!datesMatch || currentIsTaskingDates) {
        console.log("📅 Restoring catalog dates from catalogDatesRef:", storedCatalogDates);
        // Only set restoration flag if we're actually restoring catalog state (AOI matches)
        // If AOI changed, don't set the flag so search can proceed
        if (shouldRestoreCatalogState) {
          isRestoringCatalogStateRef.current = true;
        }
        setDateRange(storedCatalogDates);
        
        if (shouldRestoreCatalogState) {
          setTimeout(() => {
            isRestoringCatalogStateRef.current = false;
          }, 100);
        }
      }
    }
    
    // Now restore catalog state if AOI and dates match
    if (shouldRestoreCatalogState && catalogStateRef.current.hasState && catalogStateRef.current.dateRange) {
      const storedDateRange = catalogStateRef.current.dateRange;
      console.log("✅ Restoring catalog state - dates and AOI match", {
        storedDates: storedDateRange,
        currentDates: dateRange,
        hasImageryData: catalogStateRef.current.imageryData?.length > 0,
      });
      isRestoringCatalogStateRef.current = true;
      
      // Restore catalog state
      setImageryData(catalogStateRef.current.imageryData);
      setSelectedImages(catalogStateRef.current.selectedImages);
      setVisibleImage(catalogStateRef.current.visibleImage);
      setSelectedPolygons(catalogStateRef.current.selectedPolygons);
      setSatelliteVisibility(catalogStateRef.current.satelliteVisibility);
      
      setTimeout(() => {
        isRestoringCatalogStateRef.current = false;
      }, 100);
      
      return; // Don't set default dates if we're restoring state
    }
    
    // If AOI changed, trigger a new search with the new AOI
    if (aoiChanged && drawnCoordinates) {
      // Wait for dateRange to be set (if it was restored above)
      const finalDateRange = hasStoredCatalogDates ? catalogDatesRef.current : dateRange;
      
      if (finalDateRange && finalDateRange.startDate && finalDateRange.endDate) {
        const isTaskingDates = isTaskingDateRange(finalDateRange.startDate, finalDateRange.endDate);
        if (!isTaskingDates) {
          console.log("🔄 AOI changed - triggering catalog search with new AOI", {
            oldAOI: catalogStateRef.current.drawnCoordinates,
            newAOI: drawnCoordinates,
            dateRange: finalDateRange,
          });
          // CRITICAL: Ensure restoration flag is cleared so search can proceed
          isRestoringCatalogStateRef.current = false;
          // Use a delay to ensure dateRange state update completes first
          setTimeout(() => {
            handleSearchRequest(drawnCoordinates, finalDateRange);
          }, 200);
        }
      }
    }
    
    // No stored state BUT current dates are tasking dates - MUST set default catalog dates
    if (currentIsTaskingDates && !hasStoredCatalogDates) {
      console.log("⚠️ Current dateRange is tasking dates but no stored catalog dates - setting default catalog dates");
      isSettingDefaultDatesRef.current = true;
      
      const token = localStorage.getItem('token');
      let isHackathon = false;
      try {
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          isHackathon = (payload.sub && payload.sub.toLowerCase().includes('hackathon')) ||
                       (payload.role && payload.role.toLowerCase() === 'hackathon');
        }
      } catch (e) {
        // Ignore token parsing errors
      }
      
      let catalogDates;
      if (isHackathon) {
        catalogDates = {
          startDate: '2024-04-06',
          endDate: '2024-04-08'
        };
      } else {
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        
        catalogDates = {
          startDate: thirtyDaysAgo.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0]
        };
      }
      
      // Set catalog dates immediately and store in ref
      catalogDatesRef.current = catalogDates;
      setDateRange(catalogDates);
      
      setTimeout(() => {
        isSettingDefaultDatesRef.current = false;
      }, 100);
      
      return; // Don't continue to the default dates section below
    }
    
    // No stored state - set default dates (first time visiting catalog or after clearing state)
    console.log("📅 Setting default catalog dates (no stored state)");
    isSettingDefaultDatesRef.current = true;
    
    const token = localStorage.getItem('token');
    let isHackathon = false;
    try {
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        isHackathon = (payload.sub && payload.sub.toLowerCase().includes('hackathon')) ||
                     (payload.role && payload.role.toLowerCase() === 'hackathon');
      }
    } catch (e) {
      // Ignore token parsing errors
    }
    
    let catalogDates;
    if (isHackathon) {
      catalogDates = {
        startDate: '2024-04-06',
        endDate: '2024-04-08'
      };
    } else {
      const today = new Date();
      // Calculate exactly 30 days before today
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);
      
      catalogDates = {
        startDate: thirtyDaysAgo.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0]
      };
    }
    
    // Store in catalogDatesRef AND set state
    catalogDatesRef.current = catalogDates;
    
    // Only update if different from current dateRange
    // Don't trigger search here - let the separate useEffect handle it when dateRange changes
    if (!dateRange || dateRange.startDate !== catalogDates.startDate || dateRange.endDate !== catalogDates.endDate) {
      setDateRange(catalogDates);
    }
    
    // Reset the flag after a short delay to allow state updates to complete
    setTimeout(() => {
      isSettingDefaultDatesRef.current = false;
      console.log("✅ Default dates set, search trigger re-enabled");
    }, 100);
  }
}, [activeTab]); // Only depend on activeTab

const handleSearchRequest = async (coordinates, dates) => {
  // Generate unique request ID
  const requestId = ++requestCounter.current;
  
  console.log(`🔎 handleSearchRequest called (ID: ${requestId}):`, {
    isSearching: isSearchingRef.current,
    currentRequestId: currentRequestId.current,
    coordinates: coordinates?.length,
    dates
  });
  
  // Prevent multiple simultaneous searches - use ref for immediate check
  if (isSearchingRef.current) {
    console.log(`⏸️ Search already in progress (ID: ${currentRequestId.current}), skipping new request (ID: ${requestId})`);
    return;
  }
  
  // Set current request ID and loading state immediately
  currentRequestId.current = requestId;
  isSearchingRef.current = true;
  setLoading(true);
  
  try {
    console.log(`Starting search request ID: ${requestId} with:`, { coordinates, dates });
    const formattedCoordinates = coordinates.map(coord => ({
      lat: coord.lat || coord.latitude,
      lng: coord.lng || coord.longitude
    }));
    const requestBody = {
      coordinates: formattedCoordinates,
      dates: { startDate: dates.startDate, endDate: dates.endDate }
    };

    console.log('Request body:', requestBody);
    const token = localStorage.getItem('token');
    
    // Use try-catch for the fetch operation specifically
    try {
      const response = await fetch(SEARCH_URLS.catalog, {
        method: 'POST',        
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.status === 401) {
        alert('Session expired. Please login again.');
        localStorage.removeItem('token');
        history.push('/login');
        return;
      }

      // Don't throw errors here - handle them gracefully
      if (!response.ok) {
        if (response.status ==504){
        alert(`API Error: ${response.status}. AOI too large, Try with a smaller AOI`);
        }
        else{
          alert(`API Error: ${response.status}. Please try again with different parameters.`);
        }
        return; // Return early instead of throwing
      }

      const data = await response.json();
      setImageryData(data.items || []);
    } catch (fetchError) {
      // Handle network errors without crashing
      alert(`Network error: ${fetchError.message}. Please check your connection.`);
      console.error('Fetch error:', fetchError);
    }
  } catch (outerError) {
    // This catches any other errors in the function
    alert(`Error: ${outerError.message}`);
    console.error('Outer error:', outerError);
  } finally {
    setLoading(false);
    // Clear request ID and ref when done
    if (currentRequestId.current === requestId) {
      currentRequestId.current = null;
      isSearchingRef.current = false;
      console.log(`Completed search request ID: ${requestId}`);
    }
  }
};

const handleSaveDraft = async () => {
  try {
    // Create new order with draft status
    const response = await createOrder();
    const orderId = response.order_id;
    setCurrentOrderId(orderId);  // Save the orderI  // Save the orderId

      // Save current state as draft
      const orderData = {
        status: 'draft',
        aoi_coordinates: drawnCoordinates,
        start_date: dateRange?.startDate,
        end_date: dateRange?.endDate,
        selected_images: selectedImages
      };

      await saveDraftOrder(orderData, orderId);
      alert('Order saved as draft successfully');
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft');
    }
  };


  const handleCheckOut = () => {
    const hasIS18021 = selectedImages?.some(img => img.id?.includes('IS18021'));
  
    if (hasIS18021) {
      // Skip modal and go directly to checkout
      handleProcessingComplete({});
      return;
    }
  
    // Else, show modal as usual
    setShowProcessingModal(true);
  };
  

  const handleProcessingComplete = async (processingDetails) => {
    try {
      let orderId = currentOrderId;
      if (!orderId) {
        const response = await createOrder();
        orderId = response.id;
        setCurrentOrderId(orderId);
      }
      
      setShowProcessingModal(false);
      
      // Use push instead of replace
      history.push({
        pathname: '/checkout',
        state: { 
          orderId,
          processingDetails,
          selectedImages,
          drawnCoordinates,
          dateRange,
          orderType: activeTab === 'task' ? 'tasking' : 'archival',
          taskingData
        }
      });
    } catch (error) {
      console.error('Error during checkout:', error);
      alert('Failed to create order');
      setShowProcessingModal(false);
    }
  };

  
  return (
    
    <div className="relative">
      
      <style>
  {`
    .image-tooltip .leaflet-tooltip-content-wrapper{
      background: transparent;
      box-shadow: none;
      border: none;
      padding 0;
      marging: 0;
    }

    .image-tooltip .leaflet-popup-tip-container {
      display: none;
    }

    .image-tooltip .leaflet-popup-content {
      margin: 0;
      padding: 0;
    }
    .leaflet-tooltip.image-tooltip {
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
}

/* Optional: to hide the small triangle pointer */
.leaflet-tooltip.image-tooltip::before {
  display: none !important;
}
/* Removes white box and border from footprint tooltip */
.leaflet-tooltip.footprint-tooltip {
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* Optional: hide the default tooltip pointer triangle */
.leaflet-tooltip.footprint-tooltip::before {
  display: none !important;
}

    .custom-popup .leaflet-popup-content-wrapper {
      background: transparent;
      box-shadow: none;
      border: none;
      padding: 0;
      margin: 0;
    }
    
    .custom-popup .leaflet-popup-content {
      margin: 0;
      padding: 0;
    }
    
    .custom-popup .leaflet-popup-tip-container {
      display: none;
    }
    
    /* Remove this line to keep the close button visible
    .custom-popup .leaflet-popup-close-button {
      display: none;
    }
    */
    
    /* Style the close button to match your dark theme */
    .custom-popup .leaflet-popup-close-button {
      color: white;
      opacity: 0.7;
      margin: 2px 2px 0 0;
      z-index: 10;
    }
    
    .custom-popup .leaflet-popup-close-button:hover {
      opacity: 1;
    }
  `}
</style>

{showProcessingModal && (
  <ProcessingDeliveryModal 
    onClose={() => setShowProcessingModal(false)}
    onComplete={handleProcessingComplete}
    selectedProduct={getSelectedProduct()}
  />
)}

      <div style={{ 
        position: 'fixed',
        top: '46px',
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden'
      }}>
        <div 
          ref={mapContainerRef}
          style={{ width: '100%', height: '100%' }}
        />
      </div>

      {/* Add the MapSearchBox component here with higher z-index */}
      <div style={{ position: 'relative', zIndex: 1500 }}>
        {map && (
  <MapSearchBox 
    map={map} 
    createPolygonWithTooltip={(coords, name) => createPolygonWithTooltip(coords, map, name)}
    setDrawnCoordinates={setDrawnCoordinates}
    setActiveAOIPolygon={setActiveAOIPolygon}
    activeAOIPolygon = {activeAOIPolygon}
    onClearImagery={handleClearImagery}
    onGoClick={() => resetFiltersRef.current?.()}

  />
)}


      </div>

      {map && (
        <MapToolbar 
        onResetFilters={() => resetFiltersRef.current?.()}
          map={map}
          baseLayers={baseLayers}
          activeLayer={activeLayer}
          setActiveLayer={setActiveLayer}
          drawMode={drawMode}
          setDrawMode={setDrawMode}
          position="left"
          onAOIDeleted={handleAOIDelete}
          onDrawToolbarToggle={(isOpen) => {
            // Set isDrawing to true when toolbar opens (pencil icon clicked)
            if (isOpen) {
              isDrawingRef.current = true;
              setIsDrawing(true);
              console.log("✏️ Draw toolbar opened - setting isDrawing to true");
            } else {
              // When toolbar closes, check if there's an active draw mode
              // If no draw mode is active, set isDrawing to false
              // Otherwise, let the drawing events (pm:create, pm:disable, etc.) handle it
              if (map && !map.pm.globalDrawModeEnabled()) {
                isDrawingRef.current = false;
                setIsDrawing(false);
                console.log("✏️ Draw toolbar closed (no active draw mode) - setting isDrawing to false");
              }
            }
          }}
        />
      )}

             {showPanel && (
         <FloatingPanel
           onRegisterReset={(fn) => (resetFiltersRef.current = fn)}
           onImageSelect={handleImageSelect}
           onImageVisibilityToggle={handleImageVisibilityToggle}
           onSatelliteVisibilityToggle={handleSatelliteVisibilityToggle}
           onClose={() => setShowPanel(false)}
           position="right"
           width="450px"
           onDateRangeChange={handleDateRangeChange}
           dateRange={dateRange}
           imageryData={imageryData}
           selectedImages={selectedImages}
           onImageHover={handleImageHover}
           visibleImage={visibleImage}
           onCheckOut={handleCheckOut}
           onSaveDraft={handleSaveDraft}
           drawnCoordinates={drawnCoordinates}
           setDrawnCoordinates={setDrawnCoordinates}
           loading={loading}
           onFootprintSelect={handleFootprintSelect}
           onSaveAOI={handleSaveAOI}
           onAOISelect={(coordinates, name) => {
             handleSelectAOI(coordinates, name);
           }}
           onTaskingDataUpdate={setTaskingData}
           isRestoringTaskingState={isRestoringTaskingStateRef.current}
           cachedTaskingState={isRestoringTaskingStateRef.current ? {
             taskingDetails: taskingStateRef.current.taskingDetails,
             constellations: taskingStateRef.current.constellations,
             checkedConstellations: taskingStateRef.current.checkedConstellations,
             stripSelections: taskingStateRef.current.stripSelections || {},
             visibleConstellations: taskingStateRef.current.visibleConstellations || {},
           } : null}
           onTaskingStateReady={(state) => {
             // SIMPLE: Store state in ref whenever we have results
             if (activeTab === 'task' && dateRange && drawnCoordinates && Object.keys(state.taskingDetails).length > 0) {
               // CRITICAL: Check if dates changed - if so, this is new data that should replace old data
               const previousDateRange = taskingStateRef.current.dateRange;
               const datesChanged = !previousDateRange || 
                 previousDateRange.startDate !== dateRange.startDate ||
                 previousDateRange.endDate !== dateRange.endDate;
               
               if (datesChanged && previousDateRange) {
                 console.log('🔄 [TaskingState] Dates changed - replacing old state with new', {
                   oldDates: previousDateRange,
                   newDates: dateRange,
                   oldResultsCount: Object.keys(taskingStateRef.current.taskingDetails || {}).length,
                   newResultsCount: Object.keys(state.taskingDetails).length,
                 });
               }
               
               taskingStateRef.current = {
                 dateRange: { ...dateRange }, // Always store the latest dateRange
                 drawnCoordinates: JSON.parse(JSON.stringify(drawnCoordinates)),
                 filterKey: state.filterKey,
                 taskingDetails: JSON.parse(JSON.stringify(state.taskingDetails)), // Deep copy
                 constellations: JSON.parse(JSON.stringify(state.constellations || [])),
                 checkedConstellations: JSON.parse(JSON.stringify(state.checkedConstellations)),
                 stripSelections: JSON.parse(JSON.stringify(state.stripSelections || {})), // Store strip selections
                 visibleConstellations: JSON.parse(JSON.stringify(state.visibleConstellations || {})), // Store visibility
                 hasState: true,
               };
               console.log('💾 [TaskingState] Stored in ref', {
                 taskingDetailsCount: Object.keys(state.taskingDetails).length,
                 constellationsCount: state.constellations.length,
                 stripSelectionsCount: Object.keys(state.stripSelections || {}).length,
                 dateRange: dateRange,
                 datesChanged,
               });
             } else {
               console.log('⏸️ [TaskingState] Skipping storage', {
                 activeTab,
                 hasDateRange: !!dateRange,
                 hasCoordinates: !!drawnCoordinates,
                 hasResults: Object.keys(state.taskingDetails).length > 0,
               });
             }
           }}
           onFeasibilityTriggerReady={(triggerFn) => {
             feasibilityTriggerRef.current = triggerFn;
             console.log('✅ [ExplorePage] Feasibility trigger ready');
           }}
           onClearSelectionsReady={(clearFn) => {
             clearSelectionsRef.current = clearFn;
             console.log('✅ [ExplorePage] Clear selections ready');
           }}
           onClearResultsReady={(clearFn) => {
             clearTaskingResultsRef.current = clearFn;
             console.log('✅ [ExplorePage] Clear results ready');
           }}
           onSearch={handleSearchRequest}
           isDrawing={isDrawing}
           onTabSwitch={(tab) => {
            const currentTab = activeTab || activeTabRef.current;
            if (currentTab === 'catalog' && tab !== 'catalog') {
              storeCatalogState('tab-switch');
            }
            // Store tasking state when switching away from task tab
            // State is already stored in ref via onTaskingStateReady callback, just log it
            if (currentTab === 'task' && tab !== 'task') {
              if (taskingStateRef.current.hasState) {
                console.log('💾 [TaskingState] State already in ref, ready to restore', {
                  taskingDetailsCount: Object.keys(taskingStateRef.current.taskingDetails).length,
                });
              } else {
                console.log('⚠️ [TaskingState] No state in ref when switching tabs');
              }
            }
             setActiveTab(tab);
            activeTabRef.current = tab; // Update ref for reliable access
             clearSelectedLayers();
           
            if (map && tab === 'catalog') {
                 // Restore AOI polygon if coordinates are present
                 if (drawnCoordinates && !map.hasLayer(activeAOIPolygon)) {
                   const polygon = createPolygonWithTooltip(drawnCoordinates, map);
                   setActiveAOIPolygon(polygon);
                 }
           
              // The date range will be updated by PanelContent's useEffect
              // We'll trigger search in a useEffect that watches activeTab and dateRange
             }
           }}
           
           
         />
       )}

{selectedImages.some(img => img.id?.includes('IS18021')) && (
  <div
    style={{
      position: 'fixed',
      bottom: '10px',
      left: '280px',
      zIndex: 1500,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      padding: '10px 14px',
      borderRadius: '6px',
      color: 'white',
      fontFamily: 'sans-serif',
      fontSize: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.4)',
      minWidth: '320px',
      maxWidth: '420px'
    }}
  >
    <div style={{ marginBottom: '6px', fontWeight: '500', textAlign: 'center' }}>
      Temporal Composite NDVI
    </div>
    <div
      style={{
        height: '14px',
        background: 'linear-gradient(to right, black, darkred, red, orange, yellow, green)',
        borderRadius: '4px',
        marginBottom: '6px',
        width: '100%'
      }}
    />
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '-4px' }}>
      {[0, 100, 120, 150, 170, 200].map((tick, idx) => (
        <span key={idx} style={{ fontSize: '11px' }}>{tick}</span>
      ))}
    </div>
  </div>
)}


    </div>
  );
};

export default ExplorePage;